/*
  Product page/quick-view custom behaviors (copy-link button, quantity ->
  total price), event-delegated on `document` instead of bound per-instance
  from an inline <script> in snippets/product-info.liquid. That snippet
  renders twice - once on the real product page (normal document parse,
  inline scripts execute) and once inside the quick-view modal
  (assets/theme.js's _openQuickView sets
  modal.querySelector('.modal__inner').innerHTML = content directly) -
  and browsers never execute <script> tags inserted via innerHTML, so an
  inline <script> here would silently no-op in quick-view. Delegation on
  document works identically either way, since it only cares that a real
  click/input event bubbled up, not how the element entered the DOM.
*/
(function () {
  /*
    Per explicit request: the stock/cart-conflict warnings this file shows
    ("Magenta Pink only has 10 in stock...") were plain window.alert()
    popups - functional, but the browser's own generic system dialog
    ("shopify.trendingwholesalesarees.com says...", default OS font/
    buttons), nothing about it matches the site's own maroon/gold design.
    This is a small on-brand replacement: a centered card using the same
    button/typography look as the rest of the theme, dismissible via its
    OK button, a click on the overlay behind it, or Escape - the same
    dismiss paths a real window.alert() gives you for free, kept here on
    purpose rather than trimmed away as an unnecessary "extra".
  */
  function showThemeAlert(message) {
    var existing = document.getElementById('theme-alert-overlay');
    if (existing) existing.remove();

    var overlay = document.createElement('div');
    overlay.id = 'theme-alert-overlay';
    overlay.className = 'theme-alert-overlay';
    overlay.innerHTML =
      '<div class="theme-alert" role="alertdialog" aria-modal="true" tabindex="-1">' +
      '<p class="theme-alert__message"></p>' +
      '<button type="button" class="button button--primary theme-alert__ok">OK</button>' +
      '</div>';
    overlay.querySelector('.theme-alert__message').textContent = message;
    document.body.appendChild(overlay);

    function close() {
      overlay.remove();
      document.removeEventListener('keydown', onKeydown);
    }
    function onKeydown(event) {
      if (event.key === 'Escape') close();
    }
    overlay.addEventListener('click', function (event) {
      if (event.target === overlay) close();
    });
    overlay.querySelector('.theme-alert__ok').addEventListener('click', close);
    document.addEventListener('keydown', onKeydown);
    overlay.querySelector('.theme-alert__ok').focus();
  }

  function formatMoney(cents) {
    var template = (window.theme && window.theme.moneyFormat) || 'Rs. {{amount}}';
    var amount = (cents / 100).toFixed(2);
    return template.replace(/\{\{\s*amount\s*\}\}/, amount);
  }

  /*
    Per explicit request: Single mode's own quantity box, Mixed Bulk's
    per-colour rows, and Combo/Full Set all draw from the SAME real,
    limited stock of each colour - confirmed live repeatedly (10 Bulk +
    10 Regular + a Combo Set all of "Grey" landing in one cart at once,
    each one individually "allowed" because each mode only ever checked
    the colour's raw total stock, never what a DIFFERENT mode had already
    put in the cart). This cache is the shared, cross-mode fix: a
    best-effort snapshot of "how much of each variant id is already in
    this cart", refreshed on page load and after every real add, that
    every clamp/check below subtracts from a colour's raw stock number
    before treating anything as "available". Kept as a synchronous, in-
    memory read (not a fetch-per-keystroke) so typing/stepper clicks stay
    instant.

    Per explicit request ("need to work all checkout buttons with
    speed"), checkCompositionStock/checkBundlePickerSetStock below also
    read straight from this same cache now instead of each doing their
    own fresh /cart.js round trip first - that extra fetch was adding a
    real, measured ~300-400ms of dead time before Checkout even started
    adding the item, on top of the /cart/add.js call itself. The cache
    is kept fresh enough for this (refreshed on load and after every real
    add this page's own JS causes - see refreshCartVariantQuantityCache's
    callers), and getCartItemsForStockCheck below still falls back to a
    real fetch on the rare chance nothing has populated it yet.
  */
  var cartItemsCache = null;
  var cartVariantQuantityCache = null;
  function refreshCartVariantQuantityCache() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (response) { return response.json(); })
      .then(function (cart) {
        cartItemsCache = cart.items;
        var map = {};
        for (var i = 0; i < cart.items.length; i++) {
          var id = String(cart.items[i].variant_id);
          map[id] = (map[id] || 0) + cart.items[i].quantity;
        }
        cartVariantQuantityCache = map;
        // Per explicit report: the header cart count badge can end up
        // showing a stale-but-plausible-looking number (not just the
        // already-patched blank/NaN case - see the self-heal watcher in
        // theme.liquid) after rapid quantity edits on the cart page -
        // e.g. three line items changed in quick succession, where
        // theme.js's own native badge-refresh can apply an EARLIER
        // request's response after a LATER one if they resolve out of
        // order. This cache refresh already runs after every cart
        // mutation (see its callers) and already has the cart's real,
        // current item_count in hand here for free - writing it
        // unconditionally (not just when broken) closes that race
        // instead of only reacting to it once already visibly wrong.
        document.querySelectorAll('.header__cart-count').forEach(function (badge) {
          badge.textContent = cart.item_count;
        });
        return map;
      })
      .catch(function () {
        return cartVariantQuantityCache || {};
      });
  }
  function getCachedCartQuantity(variantId) {
    if (!variantId || !cartVariantQuantityCache) return 0;
    return cartVariantQuantityCache[String(variantId)] || 0;
  }
  function getCartItemsForStockCheck() {
    if (cartItemsCache) return Promise.resolve(cartItemsCache);
    return refreshCartVariantQuantityCache().then(function () {
      return cartItemsCache || [];
    });
  }
  /*
    Per explicit request: a Combo/Full Set already sitting in the cart
    "uses up" one of each of its own included colours too - confirmed
    live ("bulk set i have click 10 count. but comboset have already 1.
    so please check" - Mixed Bulk let 10 more Magenta Pink through even
    though a Combo Set already in the cart included one). Cart line items
    don't carry their product's tags (no composition data directly on
    /cart.js), but they DO carry `handle` - matched here against this
    page's own bundle-picker comboSets/fullSet list (each with its own
    `.handle` and `.ids`) to find any cart line that IS one of them and
    whose composition includes this specific colour id.
  */
  // Every Design's Full Set in this Sub Category (data.fullSets, one per
  // Design), plus the legacy single data.fullSet - deduped by handle.
  function allBundlePickerFullSets(data) {
    var list = [];
    var seen = {};
    var add = function (set) {
      if (set && set.handle && !seen[set.handle]) {
        seen[set.handle] = true;
        list.push(set);
      }
    };
    add(data.fullSet);
    if (data.fullSets) {
      for (var key in data.fullSets) {
        if (Object.prototype.hasOwnProperty.call(data.fullSets, key)) add(data.fullSets[key]);
      }
    }
    return list;
  }

  function computeComboFullSetContribution(colourId, cartItems, state) {
    if (!colourId || !state || !cartItems) return 0;
    var sets = (state.data.comboSets ? state.data.comboSets.slice() : []).concat(allBundlePickerFullSets(state.data));
    var handleToIds = {};
    for (var i = 0; i < sets.length; i++) {
      if (sets[i].handle) handleToIds[sets[i].handle] = sets[i].ids || [];
    }
    var contribution = 0;
    for (var j = 0; j < cartItems.length; j++) {
      var ids = handleToIds[cartItems[j].handle];
      if (ids && ids.indexOf(colourId) !== -1) {
        contribution += cartItems[j].quantity;
      }
    }
    return contribution;
  }
  function getCachedComboFullSetContribution(colourId, state) {
    if (!cartItemsCache) return 0;
    return computeComboFullSetContribution(colourId, cartItemsCache, state);
  }
  /*
    Per explicit request: freeing up stock by removing/reducing a line in
    the mini-cart or main cart only showed the correct "Only N in stock"
    ceiling/message on a product page after a full reload - refreshing
    the cache above (see refreshCartOnMutation below) isn't enough by
    itself, since nothing then re-runs the clamp/message logic against
    the now-current cache for whatever's already visible on screen.
    Re-invoking updateTotalPrice/clampBulkQtyInput here for every
    quantity control currently on the page makes that update happen
    live, the instant the cache refreshes, with no reload needed.
  */
  /*
    Per explicit request: a colour whose entire real stock is already
    sitting in the cart (its own Regular/Bulk variant, or "used up" by
    another Combo/Full Set already in the cart - see
    computeBundleFullSetContribution above) should show the same
    greyed-out/unselectable "out of stock" treatment in the Combo Set and
    Full Set colour pickers that a colour with 0 real stock already gets
    server-side (bundle-picker.liquid's is_out_of_stock) - confirmed live
    ("suppose one saree add all stock in cart... that saree show in
    fullset and comboset also [as out of stock]"). Toggling the SAME
    data-bundle-picker-out-of-stock attribute the server-side case uses
    (not just a CSS class) means setBundlePickerMode's own force-check-
    everything-for-Full-Set loop already skips it for free, with no
    separate change needed there - it only re-checks/re-enables boxes
    that DON'T carry this attribute. A colour with 0 real stock needs no
    separate "permanent" skip here either - inCart (always >= 0) can never
    be less than a quantity of 0, so the math below naturally keeps it
    disabled forever on its own, same end result as the dedicated
    server-side case.
  */
  function addStockBadge(container) {
    if (!container || container.querySelector('.bundle-picker__stock-badge')) return;
    var badge = document.createElement('span');
    badge.className = 'bundle-picker__stock-badge';
    badge.textContent = 'Out of Stock';
    container.appendChild(badge);
  }
  function removeStockBadge(container) {
    var badge = container ? container.querySelector('.bundle-picker__stock-badge') : null;
    if (badge) badge.remove();
  }
  function refreshBundlePickerColourAvailability(root) {
    var state = getBundlePickerState(root);
    if (!state || !cartItemsCache) return;
    for (var i = 0; i < state.data.colours.length; i++) {
      var colour = state.data.colours[i];
      if (typeof colour.quantity !== 'number') continue;
      var relevantVariantIds = [String(colour.variantId)].concat(collectBulkVariantIds(colour));
      var inCart = 0;
      for (var j = 0; j < cartItemsCache.length; j++) {
        if (relevantVariantIds.indexOf(String(cartItemsCache[j].variant_id)) !== -1) {
          inCart += cartItemsCache[j].quantity;
        }
      }
      inCart += getCachedComboFullSetContribution(colour.id, state);
      var isFullyCommitted = inCart >= colour.quantity;

      // The Combo Set/Full Set colour pill checkbox for this colour.
      var checkbox = root.querySelector('[data-bundle-picker-colour][value="' + colour.id + '"]');
      if (checkbox) {
        var label = checkbox.closest('.bundle-picker__colour-option');
        // Mirrors bundle-picker.liquid's own server-rendered
        // <span class="bundle-picker__stock-badge">Out of Stock</span>
        // (shown for a colour with 0 real stock) - confirmed live that the
        // greyed-out pill alone, with no text, wasn't clear enough on its
        // own ("jsut show No stock").
        if (isFullyCommitted) {
          checkbox.checked = false;
          checkbox.disabled = true;
          checkbox.setAttribute('data-bundle-picker-out-of-stock', '');
          if (label) {
            label.classList.add('bundle-picker__colour-option--out-of-stock');
            addStockBadge(label);
          }
        } else if (checkbox.hasAttribute('data-bundle-picker-out-of-stock')) {
          checkbox.disabled = root.getAttribute('data-bundle-picker-mode') === 'full';
          checkbox.removeAttribute('data-bundle-picker-out-of-stock');
          if (label) {
            label.classList.remove('bundle-picker__colour-option--out-of-stock');
            removeStockBadge(label);
          }
        }
      }

      // The same colour's row in the Mixed Bulk table - confirmed live this
      // needed the identical treatment ("this are also show and blur no
      // stock"), not just the colour-picker checkboxes above.
      var bulkInput = root.querySelector('[data-bundle-picker-bulk-qty][data-variant-id="' + colour.variantId + '"]');
      if (bulkInput) {
        var bulkRow = bulkInput.closest('tr');
        var decreaseBtn = bulkRow ? bulkRow.querySelector('[data-bundle-picker-bulk-qty-decrease]') : null;
        var increaseBtn = bulkRow ? bulkRow.querySelector('[data-bundle-picker-bulk-qty-increase]') : null;
        var colourCell = bulkRow ? bulkRow.querySelector('.bundle-picker__bulk-colour-cell') : null;
        if (isFullyCommitted) {
          bulkInput.value = '0';
          bulkInput.disabled = true;
          if (decreaseBtn) decreaseBtn.disabled = true;
          if (increaseBtn) increaseBtn.disabled = true;
          if (bulkRow) bulkRow.classList.add('bundle-picker__bulk-row--out-of-stock');
          addStockBadge(colourCell);
        } else if (bulkRow && bulkRow.classList.contains('bundle-picker__bulk-row--out-of-stock')) {
          // colour.quantity is always > 0 here (a real 0-stock row can never
          // reach this branch - isFullyCommitted is always true when
          // quantity is 0, since inCart can never be negative), so this is
          // always a previously dynamically-disabled row being freed up
          // again, never the permanent server-rendered case.
          bulkInput.disabled = false;
          if (decreaseBtn) decreaseBtn.disabled = false;
          if (increaseBtn) increaseBtn.disabled = false;
          if (bulkRow) bulkRow.classList.remove('bundle-picker__bulk-row--out-of-stock');
          removeStockBadge(colourCell);
        }
      }
    }
    if (typeof updateBundlePickerBulkSummary === 'function' && root.getAttribute('data-bundle-picker-mode') === 'bulk') {
      updateBundlePickerBulkSummary(root);
    }
  }

  function reRenderVisibleQuantityUiFromCache() {
    document.querySelectorAll('.product-block-list').forEach(function (root) {
      if (root.querySelector('.quantity-selector--product .quantity-selector__value')) {
        updateTotalPrice(root);
      }
    });
    document.querySelectorAll('[data-bundle-picker-bulk-qty]').forEach(function (input) {
      clampBulkQtyInput(input);
    });
    document.querySelectorAll('[data-bundle-picker][data-bundle-picker-mode="bulk"]').forEach(function (root) {
      updateBundlePickerBulkSummary(root);
    });
    document.querySelectorAll('[data-bundle-picker]').forEach(function (root) {
      refreshBundlePickerColourAvailability(root);
      updateBundlePickerResult(root);
    });
  }
  function refreshCartOnMutation() {
    return refreshCartVariantQuantityCache().then(reRenderVisibleQuantityUiFromCache);
  }
  // Runs once on page load too (not just after a later mutation) - a colour
  // that was already fully committed to the cart from an earlier visit
  // needs to show as unavailable immediately, not just after this page's
  // own first add/remove.
  refreshCartVariantQuantityCache().then(function () {
    document.querySelectorAll('[data-bundle-picker]').forEach(function (root) {
      refreshBundlePickerColourAvailability(root);
      updateBundlePickerResult(root);
    });
  });
  document.body.addEventListener('product:added', refreshCartOnMutation);
  /*
    Per explicit request: removing/decreasing a line in the mini-cart or
    main cart page didn't free up its stock for this cache until a full
    page reload - "Remove" (theme.js's own _updateQuantity, bound to
    [data-action="decrease-quantity"]/[data-action="increase-quantity"])
    never fires "product:added" (that only ever means a real *addition*),
    so the cache above kept counting an item as still "in cart" right
    after it was removed. theme.js dispatches a generic
    "theme:loading:end" once ANY native cart-mutating action - add OR
    remove/change, from the mini-cart drawer or the full cart page alike -
    finishes its fetch AND re-renders the cart section (see _addToCart and
    _updateQuantity, both wrapped in theme:loading:start/end) - a single
    reliable hook that covers every native mutation path this file
    doesn't already know about directly, without needing a second,
    action-specific listener per button.
  */
  document.addEventListener('theme:loading:end', refreshCartOnMutation);

  /*
    Per explicit request: buying 10+ of the very same colour knocks ₹50
    off each unit of THAT colour specifically (not the whole order, not
    Shopify's own Automatic Discount engine - "dont use shopify discount.
    i need my own logic"). Several tiers/slabs can be configured now (e.g.
    10+ -> ₹100 off, 20+ -> ₹150 off, 30+ -> ₹200 off) - whichever tier a
    shopper's quantity reaches applies to their WHOLE quantity, not a
    graduated/stacked discount (per explicit request). The real tiers come
    from the backend's resolved Category/Sub Category/Design rule (falling
    back to the flat default - see backend's resolveQuantityDiscountSettings),
    written onto the picker root's own data-quantity-discount-tiers JSON
    attribute (see bundle-picker.liquid) in the same combo_picker.data
    metafield comboSetMinCount etc. already come from - not a second
    lookup, just reading what's already on the page. Missing/empty
    (feature disabled entirely, or a picker-less page) resolves to "never
    triggers" rather than silently reactivating a merchant-disabled
    feature with a guessed default. Shared by both the native product
    page's own quantity/total-price display (updateTotalPrice, Single
    mode) and Mixed Bulk's own table total (updateBundlePickerBulkSummary)
    - there's only ever one bundle-picker per page, so both read the same
    single source of truth.
  */
  function getQuantityDiscountTiers() {
    var picker = document.querySelector('[data-bundle-picker]');
    var raw = picker ? picker.getAttribute('data-quantity-discount-tiers') : null;
    var tiers;
    try {
      tiers = raw ? JSON.parse(raw) : [];
    } catch (e) {
      tiers = [];
    }
    if (!tiers || !tiers.length) return [];
    var cleaned = [];
    for (var i = 0; i < tiers.length; i++) {
      var minQty = parseInt(tiers[i].minQuantity, 10);
      var perItem = parseFloat(tiers[i].discountPerItem);
      if (minQty > 0 && perItem > 0) cleaned.push({ minQty: minQty, perItem: perItem });
    }
    cleaned.sort(function (a, b) { return a.minQty - b.minQty; });
    return cleaned;
  }

  // The highest tier a given quantity has reached (ascending-sorted
  // tiers), or null if it hasn't reached even the lowest one. "Highest
  // reached applies to the whole quantity" - per explicit request, not a
  // graduated/stacked discount.
  function resolveQuantityDiscountTier(tiers, qty) {
    var resolved = null;
    for (var i = 0; i < tiers.length; i++) {
      if (qty >= tiers[i].minQty) resolved = tiers[i];
    }
    return resolved;
  }

  // Every real Shopify variant id a colour's bulk tiers list represents -
  // one bulk-tiers JSON blob (same shape backend's quantityDiscountSync.ts
  // writes) covers however many tiers are configured, not just one.
  function parseBulkTiers(raw) {
    var tiers;
    try {
      tiers = raw ? JSON.parse(raw) : [];
    } catch (e) {
      tiers = [];
    }
    return tiers || [];
  }

  function collectBulkVariantIds(colour) {
    var ids = [];
    var tiers = colour && colour.bulkTiers;
    if (tiers) {
      for (var i = 0; i < tiers.length; i++) ids.push(String(tiers[i].variantId));
    }
    return ids;
  }

  // Mirrors resolveQuantityDiscountTier but over a bulk-tiers list (each
  // entry already a real variant id, not just a ₹ amount) - returns the
  // variant id to actually submit for this quantity, or null if it hasn't
  // reached even the lowest tier (submit the regular variant instead).
  function resolveBulkVariantIdForQty(bulkTiers, qty) {
    var tier = resolveBulkTierForQty(bulkTiers, qty);
    return tier ? String(tier.variantId) : null;
  }

  // Same resolution, returning the whole tier object (so its real `price`
  // is available too, not just the variant id) - confirmed live
  // ("not show discount here 10 quantity is there right"): a plain product
  // with no bundle-picker on its page at all has no
  // [data-bundle-picker][data-quantity-discount-tiers] for
  // getQuantityDiscountTiers to read (that's a bundle-picker-rooted
  // attribute - see its own comment), so updateTotalPrice's displayed
  // price/discount silently never moved even though this exact product's
  // OWN data-bulk-tiers (read below, always present regardless of any
  // bundle-picker) already has real, per-tier prices and was ALREADY
  // correctly swapping the submitted variant id past each threshold - the
  // checkout would likely have charged correctly while the price shown on
  // the page never reflected it. data-bulk-tiers is now the authoritative
  // source for the discount itself too, not just which variant id to
  // submit.
  function resolveBulkTierForQty(bulkTiers, qty) {
    var resolved = null;
    var resolvedMinQty = -1;
    for (var i = 0; i < bulkTiers.length; i++) {
      var minQty = parseInt(bulkTiers[i].minQuantity, 10);
      if (qty >= minQty && minQty > resolvedMinQty) {
        resolvedMinQty = minQty;
        resolved = bulkTiers[i];
      }
    }
    return resolved;
  }

  // The given variant's inventory info, read fresh from the
  // <script data-product-json> blob this snippet already renders (keyed by
  // variant id) rather than baked into a static attribute - so it stays
  // correct across a client-side variant switch (colour, etc.) without
  // needing to hook into theme.js's own variant-change handling. Defaults
  // to whichever variant the form's own select is currently on, but a
  // specific variantId can be passed instead (see clampQuantityInput's own
  // comment for why that matters once the quantity-discount "Bulk 10+ qty"
  // variant swap is in play).
  function getVariantInventory(root, variantId) {
    var productJsonEl = root.querySelector('[data-product-json]');
    var select = root.querySelector('select[name="id"]');
    if (!productJsonEl || !select) return null;
    var id = variantId || select.value;
    var data;
    try {
      data = JSON.parse(productJsonEl.textContent);
    } catch (e) {
      return null;
    }
    return (data.inventories && data.inventories[id]) || null;
  }

  // Neither theme.js's native stepper (_onIncrease/_onDecrease) nor typing
  // into the box enforces any ceiling - a customer could otherwise punch in
  // (or click up to) far more than the variant actually has in stock. Only
  // clamps when the variant is inventory-tracked with oversell disallowed
  // ("deny"); an untracked variant or one that explicitly allows overselling
  // ("continue") is left unbounded, matching the cart drawer's existing
  // data-max-quantity convention (snippets/mini-cart-content.liquid,
  // sections/main-cart.liquid).
  //
  // Always checks the *regular* variant's own stock (data-regular-variant-id,
  // set once at page load - see product-info.liquid), never whichever
  // variant happens to be selected right now - the quantity-discount "Bulk
  // 10+ qty" variant this form auto-switches to past the threshold is
  // deliberately untracked in Shopify (its own stock is reconciled
  // separately, see backend's paid-order poll), so checking ITS inventory
  // here would find nothing and silently stop enforcing any ceiling at all
  // past 10 - confirmed live ("only 10 available but why enter 100"). Real
  // physical stock is the regular variant's, no matter which variant ID
  // actually gets submitted for pricing.
  //
  // Returns true when the value the shopper was trying to set actually got
  // clamped down (i.e. they tried to go past available stock) - per
  // explicit request, that moment needs a visible "no more stock" message,
  // not just a silently-corrected number that looks like "+" did nothing.
  function clampQuantityInput(root, input) {
    if (!input) return false;
    var totalPriceWrap = root.querySelector('[data-product-total-price]');
    var regularVariantId = totalPriceWrap ? totalPriceWrap.getAttribute('data-regular-variant-id') : null;
    var bulkTiers = totalPriceWrap ? parseBulkTiers(totalPriceWrap.getAttribute('data-bulk-tiers')) : [];
    var inventory = getVariantInventory(root, regularVariantId);
    if (!inventory || inventory.inventory_management == null || inventory.inventory_policy !== 'deny') return false;
    var max = inventory.inventory_quantity;
    if (typeof max !== 'number' || max < 1) return false;
    // Per explicit request: this colour's Regular and every Bulk tier
    // variant (and any Combo/Full Set already holding it) all draw from
    // this same real stock number - subtract whatever's already sitting
    // in the cart across ALL of THIS colour's own variants, AND whatever
    // a Combo/Full Set already in the cart is using of this same colour,
    // before treating the rest as available here too.
    var alreadyInCart = getCachedCartQuantity(regularVariantId);
    for (var bi = 0; bi < bulkTiers.length; bi++) {
      alreadyInCart += getCachedCartQuantity(String(bulkTiers[bi].variantId));
    }
    var pickerRoot = root.querySelector('[data-bundle-picker]');
    var bundleState = pickerRoot ? getBundlePickerState(pickerRoot) : null;
    if (bundleState) {
      var thisColour = bundleState.data.colours.filter(function (c) { return c.variantId === regularVariantId; })[0];
      if (thisColour) alreadyInCart += getCachedComboFullSetContribution(thisColour.id, bundleState);
    }
    max = Math.max(0, max - alreadyInCart);
    var value = parseInt(input.value, 10) || 1;
    if (value > max) {
      input.value = max;
      return true;
    }
    return false;
  }

  function updateTotalPrice(root) {
    var totalPriceEl = root.querySelector('[data-total-price-value]');
    var totalPriceWrap = root.querySelector('[data-product-total-price]');
    var quantityInput = root.querySelector('.quantity-selector--product .quantity-selector__value');
    if (!totalPriceEl || !totalPriceWrap || !quantityInput) return;
    var wasClamped = clampQuantityInput(root, quantityInput);
    var stockLimitEl = root.querySelector('[data-product-stock-limit]');
    if (stockLimitEl) {
      if (wasClamped) {
        stockLimitEl.hidden = false;
        stockLimitEl.textContent = 'Only ' + quantityInput.value + ' in stock - can\'t add more right now.';
      } else {
        stockLimitEl.hidden = true;
        stockLimitEl.textContent = '';
      }
    }
    var unitPrice = parseInt(totalPriceWrap.getAttribute('data-unit-price'), 10) || 0;
    var qty = parseInt(quantityInput.value, 10) || 1;

    // Per explicit request: buying 10+ of this same saree knocks a fixed
    // ₹ amount off each unit, with bigger tiers for higher quantities
    // (e.g. 10+ -> ₹100 off, 20+ -> ₹150 off, 30+ -> ₹200 off - "in
    // single, select quantity 10+ each saree reduce 50 rupees ... dont use
    // shopify discount. i need my own logic"). Whichever tier the quantity
    // reaches applies to the WHOLE quantity, not a graduated/stacked
    // discount.
    //
    // data-bulk-tiers (this exact product/variant's own metafield, always
    // present regardless of whether a bundle-picker exists on the page)
    // is the authoritative source, since it already carries each tier's
    // REAL price - the generic getQuantityDiscountTiers() table (rooted on
    // a [data-bundle-picker] element) is only a fallback for whatever a
    // picker-driven page might need beyond that; a plain product with no
    // bundle-picker at all has no picker element for it to read from.
    var bulkTiersForDiscount = parseBulkTiers(totalPriceWrap.getAttribute('data-bulk-tiers'));
    var resolvedBulkTier = resolveBulkTierForQty(bulkTiersForDiscount, qty);
    var quantityDiscountCents;
    if (resolvedBulkTier) {
      quantityDiscountCents = Math.max(0, unitPrice - Math.round(parseFloat(resolvedBulkTier.price) * 100));
    } else {
      var quantityDiscountTiers = getQuantityDiscountTiers();
      var quantityDiscountTier = resolveQuantityDiscountTier(quantityDiscountTiers, qty);
      quantityDiscountCents = quantityDiscountTier ? quantityDiscountTier.perItem * 100 : 0;
    }
    var effectiveUnitPrice = Math.max(0, unitPrice - quantityDiscountCents);
    totalPriceEl.textContent = formatMoney(effectiveUnitPrice * qty);

    // Per explicit request: spell out the math behind the Total Price
    // above ("this much saree this cost this much discount so total is
    // this") - display only, reads straight off the same unitPrice/qty/
    // quantityDiscountCents this function already computed for Total
    // Price/You saved, so it can never disagree with them.
    var breakdownEl = root.querySelector('[data-product-price-breakdown]');
    if (breakdownEl) {
      if (qty > 1) {
        breakdownEl.hidden = false;
        var breakdownText = qty + ' sarees × ' + formatMoney(unitPrice) + ' = ' + formatMoney(unitPrice * qty);
        if (quantityDiscountCents > 0) {
          breakdownText += ' − ' + formatMoney(quantityDiscountCents * qty) + ' discount';
        }
        breakdownEl.textContent = breakdownText;
      } else {
        breakdownEl.hidden = true;
        breakdownEl.textContent = '';
      }
    }

    var savingsEl = root.querySelector('[data-product-savings]');
    if (savingsEl) {
      if (quantityDiscountCents > 0) {
        savingsEl.hidden = false;
        savingsEl.textContent = 'You saved ' + formatMoney(quantityDiscountCents * qty);
      } else {
        savingsEl.hidden = true;
        savingsEl.textContent = '';
      }
    }

    // Per explicit request: this has to actually charge less at checkout,
    // not just preview it - swap the form's real submitted variant id to
    // whichever tier's cheaper "Bulk N+ qty" variant the quantity has
    // reached (a real, separately-priced Shopify variant - see backend's
    // quantityDiscountSync.ts), and back to the regular variant below the
    // lowest tier. data-bulk-tiers is "[]" whenever this variant has no
    // bulk counterparts (sync hasn't reached it yet, feature disabled),
    // in which case this is a no-op and only the regular variant is ever
    // submitted, same as before this existed. The <select name="id"> already
    // has an <option> for each bulk tier variant for free - they're real
    // product variants, so Shopify's own variant loop already rendered one.
    var regularVariantId = totalPriceWrap.getAttribute('data-regular-variant-id');
    if (regularVariantId && bulkTiersForDiscount.length) {
      var variantSelect = root.querySelector('select[name="id"]');
      if (variantSelect) {
        var desiredVariantId = resolveBulkVariantIdForQty(bulkTiersForDiscount, qty) || regularVariantId;
        if (variantSelect.value !== desiredVariantId && variantSelect.querySelector('option[value="' + desiredVariantId + '"]')) {
          variantSelect.value = desiredVariantId;
          variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }
  }

  function onQuantityChanged(event) {
    if (!event.target.matches || !event.target.matches('.quantity-selector--product .quantity-selector__value')) return;
    var root = event.target.closest('.product-block-list');
    if (root) updateTotalPrice(root);
  }
  document.addEventListener('input', onQuantityChanged);
  document.addEventListener('change', onQuantityChanged);

  document.addEventListener('click', function (event) {
    var stepper = event.target.closest && event.target.closest('[data-action="increase-picker-quantity"], [data-action="decrease-picker-quantity"]');
    if (!stepper) return;
    var root = stepper.closest('.product-block-list');
    // Native stepper JS updates the input's value in its own click handler
    // first - read the result on the next tick rather than the stale value.
    if (root) setTimeout(function () { updateTotalPrice(root); }, 0);
  });

  /*
    A Combo/Full Set bundle's gallery has one photo per colour, and
    snippets/bundle-colours.liquid renders each colour name as its own
    [data-bundle-colour] chip - this highlights (solid background box) the
    chip matching whichever photo is currently shown, so a shopper flipping
    through the gallery can tell which colour they're looking at.

    Matches by comparing the active carousel item's <img alt> (set by the
    backend to the colour name at bundle-creation time - see
    bundleSync.ts's createBundleForTarget) against each chip's
    data-bundle-colour, rather than by position, so the photo order and
    the colour-list order never need to match up.

    A single document-level observer (rather than one per product root)
    covers both the real product page (present at parse time) and the
    quick-view modal (whose markup is injected into the DOM later via
    innerHTML - a per-instance listener attached at parse time would never
    see it) without needing a separate init hook for each.
  */
  var galleryObserver = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var item = mutations[i].target;
      if (!item.classList || !item.classList.contains('product-gallery__carousel-item')) continue;
      if (!item.classList.contains('is-selected')) continue;

      var root = item.closest('.product-block-list');
      var img = item.querySelector('img');
      if (!root || !img || !img.alt) continue;

      // Only one photo is ever shown at a time, so only one chip should
      // ever be highlighted - clear every chip first rather than just the
      // previously-matched one, in case a stale highlight was left behind.
      var chips = root.querySelectorAll('[data-bundle-colour]');
      for (var j = 0; j < chips.length; j++) {
        chips[j].classList.remove('bundle-colour-chip--active');
      }
      for (var k = 0; k < chips.length; k++) {
        if (chips[k].getAttribute('data-bundle-colour') === img.alt) {
          chips[k].classList.add('bundle-colour-chip--active');
        }
      }
    }
  });
  galleryObserver.observe(document.body, { attributes: true, attributeFilter: ['class'], subtree: true });

  /*
    snippets/bundle-picker.liquid: a saree's page lets a shopper pick
    colours from its Sub Category and see whichever pre-generated Combo
    Set (exactly 3) or Full Set (all of them) that selection matches.

    The match is purely data-driven: comboSets/fullSet from the embedded
    JSON are indexed once by their sorted, pipe-joined colour-id set, and
    a selection is looked up the same way. Any selection that isn't
    exactly one comboSet's ids or exactly the fullSet's ids (e.g. 4 out of
    6 colours) simply isn't a key in the index, so it falls through to
    "no match" with no separate size check needed to enforce that.
  */
  function getBundlePickerState(root) {
    if (root._bundlePickerState) return root._bundlePickerState;
    var jsonEl = root.querySelector('[data-bundle-picker-json]');
    if (!jsonEl) return null;
    var data;
    try {
      data = JSON.parse(jsonEl.textContent);
    } catch (e) {
      return null;
    }
    // data.fullSet is one slot for the whole Sub Category (the last
    // Design's Full Set) - with several Designs under one title, use this
    // picker's own Design's entry from data.fullSets instead, or Full Set
    // mode offers a different Design's sarees. Older payloads without
    // fullSets keep the single fullSet as before.
    var pickerDesign = root.getAttribute('data-bundle-picker-design') || '';
    if (data.fullSets && pickerDesign) data.fullSet = data.fullSets[pickerDesign] || null;
    var index = {};
    for (var i = 0; i < data.comboSets.length; i++) {
      index[data.comboSets[i].ids.slice().sort().join('|')] = data.comboSets[i];
    }
    if (data.fullSet) index[data.fullSet.ids.slice().sort().join('|')] = data.fullSet;
    var state = { data: data, index: index };
    root._bundlePickerState = state;
    return state;
  }

  // Shared by every "no exact Combo/Full Set, fall back to Mixed Bulk"
  // path below (Full Set short a sold-out colour, Full Set entirely
  // unavailable, Combo Set overflow past its fixed size) - same price/
  // savings styling as the real Combo/Full Set match block
  // (product-form__price-breakdown, price-list/price--highlight,
  // product-meta__savings-badge), computed for 1 of each variant id given
  // (exactly what the "Buy..." button pre-fills), using each colour's own
  // bulk-row data-unit-price/data-bulk-tiers/data-unit-savings - the same
  // per-row tier math as updateBundlePickerBulkSummary's live table total.
  function getBulkPricePreviewHtml(root, variantIds) {
    var count = variantIds.length;
    if (count < 1) return '';
    var fallbackTiers = getQuantityDiscountTiers();
    var totalPrice = 0;
    // Original (pre-discount) total, same convention as the real Combo/Full
    // Set match's own match.compareAtPrice - each row's data-unit-savings is
    // already that colour's own (compare-at - selling price) per unit (see
    // bundle-picker.liquid's bulk_savings_per_item), so adding it back onto
    // data-unit-price recovers that colour's real compare-at price.
    var totalOriginalPrice = 0;
    var totalSavings = 0;
    for (var i = 0; i < variantIds.length; i++) {
      var input = root.querySelector('[data-bundle-picker-bulk-qty][data-variant-id="' + CSS.escape(variantIds[i]) + '"]');
      if (!input) continue;
      var unitPrice = parseFloat(input.getAttribute('data-unit-price')) || 0;
      var unitSavings = parseFloat(input.getAttribute('data-unit-savings')) || 0;
      var rowTiers = parseBulkTiers(input.getAttribute('data-bulk-tiers'));
      var rowTier = rowTiers.length ? resolveBulkTierForQty(rowTiers, count) : null;
      var rowTierPrice = rowTier ? parseFloat(rowTier.price) : NaN;
      if (isNaN(rowTierPrice)) rowTier = resolveQuantityDiscountTier(fallbackTiers, count);
      var quantityDiscount = rowTier ? (!isNaN(rowTierPrice) ? unitPrice - rowTierPrice : rowTier.perItem) : 0;
      var effectiveUnitPrice = Math.max(0, unitPrice - quantityDiscount);
      totalPrice += effectiveUnitPrice;
      totalOriginalPrice += unitPrice + unitSavings;
      totalSavings += unitSavings + quantityDiscount;
    }

    // Same "N sarees x price = subtotal" breakdown already shown for a real
    // Combo/Full Set match, per explicit request ("this type of content
    // needed for fullset and combo also").
    var breakdownHtml = '';
    if (count > 0 && totalOriginalPrice > 0) {
      var perPieceCents = (totalOriginalPrice * 100) / count;
      breakdownHtml =
        '<p class="product-form__price-breakdown text--small">' +
        count + ' sarees × ' + formatMoney(perPieceCents) + ' = ' + formatMoney(totalOriginalPrice * 100) +
        '</p>';
    }

    var priceHtml =
      '<span class="price price--highlight"><span class="money">' + formatMoney(totalPrice * 100) + '</span></span>';
    if (totalOriginalPrice > totalPrice) {
      priceHtml +=
        '<span class="price price--compare"><span class="money">' + formatMoney(totalOriginalPrice * 100) + '</span></span>';
    }

    var html = breakdownHtml + '<div class="product-form__price-info price-list">' + priceHtml + '</div>';
    if (totalSavings > 0) {
      html +=
        '<p class="product-meta__savings-badge">' +
        '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20.59 13.41 11 3.83A2 2 0 0 0 9.59 3.24L3.24 9.59A2 2 0 0 0 3.83 11l9.58 9.59a2 2 0 0 0 2.83 0l4.35-4.35a2 2 0 0 0 0-2.83Z"></path><circle cx="7.5" cy="7.5" r="1.5" fill="currentColor" stroke="none"></circle></svg>' +
        'You saved <span class="money">' + formatMoney(totalSavings * 100) + '</span>' +
        '</p>';
    }
    return html;
  }

  function updateBundlePickerResult(root) {
    var state = getBundlePickerState(root);
    var resultEl = root.querySelector('[data-bundle-picker-result]');
    if (!state || !resultEl) return;

    var checkboxes = root.querySelectorAll('[data-bundle-picker-colour]');
    var selected = [];
    for (var i = 0; i < checkboxes.length; i++) {
      if (checkboxes[i].checked) selected.push(checkboxes[i].value);
    }

    var mode = root.getAttribute('data-bundle-picker-mode') || 'combo';

    // Single mode never matches a Combo/Full Set bundle at all - it just
    // links straight to whichever one colour is checked, using its own
    // price and product handle (already in state.data.colours, no bundle
    // lookup needed). Single-select exclusivity itself is enforced by the
    // checkbox change listener below, not here - by the time this runs,
    // at most one should ever be checked in this mode.
    if (mode === 'single') {
      var selectedColour = selected.length === 1 ? state.data.colours.filter(function (c) { return c.id === selected[0]; })[0] : null;
      // Per explicit request: Single mode never shows its own price/"View
      // this saree" block at all - checking a colour navigates straight
      // there (or, if it's the colour already matching this very page, it
      // just stays here), so this result area would either flash for an
      // instant before the redirect or sit there uselessly duplicating
      // info already on the page. Always keep it hidden in this mode.
      resultEl.hidden = true;
      resultEl.innerHTML = '';
      if (selectedColour) {
        // Per explicit request: checking a colour in Single mode goes
        // straight to that saree's real product page, without needing to
        // click "View this saree" separately (a same-page swap was tried
        // first but confirmed live to break the theme's own JS-driven
        // gallery/carousel layout - see the comment above the click
        // handler further down). Guarded by handle so switching mode/
        // re-running this for an unchanged selection doesn't keep
        // re-navigating.
        //
        // _bundlePickerSkipAutoNavigate is set once, right before the
        // very first call on page load, for the one box the Liquid side
        // already pre-checks to match *this* page's own product (see
        // bundle-picker.liquid's current_colour_prefix) - without it,
        // this would immediately navigate to the page it's already on,
        // in an infinite reload loop.
        if (root._bundlePickerSkipAutoNavigate) {
          root._bundlePickerSkipAutoNavigate = false;
          root._bundlePickerAutoOpenedHandle = selectedColour.handle;
        } else if (root._bundlePickerAutoOpenedHandle !== selectedColour.handle) {
          root._bundlePickerAutoOpenedHandle = selectedColour.handle;
          window.location.href = '/products/' + selectedColour.handle;
        }
      } else {
        root._bundlePickerAutoOpenedHandle = null;
      }
      return;
    }

    // Full Set mode has nothing to look up if this Sub Category doesn't
    // have enough colours for one yet (see computeBundleTargets) - every
    // checkbox is still forced checked (that's what "Full Set" means),
    // but there's no bundle behind that selection to link to, so say so
    // plainly instead of silently showing nothing.
    // Mixed Bulk mode has its own table + "Add selected to cart" button
    // (see the click handler further down) - it never uses this shared
    // Combo/Full Set result area at all.
    if (mode === 'bulk') {
      resultEl.hidden = true;
      resultEl.innerHTML = '';
      return;
    }

    if (mode === 'full' && !state.data.fullSet) {
      resultEl.hidden = false;
      resultEl.innerHTML = '<p class="bundle-picker__unavailable">Full Set isn’t available yet for this Sub Category.</p>';
      return;
    }

    // Per explicit request: Full Set mode always means "the one Full Set
    // this Sub Category has" - full stop. It's looked up directly here,
    // never through the same selected-ids match Combo Set uses below,
    // because an out-of-stock colour (see is_out_of_stock in
    // bundle-picker.liquid) is excluded from ever being checked at all,
    // which meant the checked id set could stop exactly matching the
    // Full Set's own (all-colours) composition key - and its price/
    // savings/"View this Full Set" link would silently vanish even though
    // the Full Set itself is still exactly as available as ever.
    //
    // That "still exactly as available as ever" assumption breaks once a
    // colour can go out of stock mid-session from THIS shopper's own cart
    // actions (see refreshBundlePickerColourAvailability) rather than only
    // ever being a fixed, already-known state at page load - confirmed live
    // ("in full set two is out of stock but price and contents show
    // wrong"): the Full Set is one fixed Shopify product requiring all of
    // fullSet.ids, so if even one of them is no longer actually gettable,
    // that exact product can no longer be fulfilled either, no matter how
    // "available" it still looks from a stale page-load snapshot.
    var fullSetStillAvailable = true;
    if (mode === 'full' && state.data.fullSet) {
      for (var fi = 0; fi < state.data.fullSet.ids.length; fi++) {
        var fsCheckbox = root.querySelector('[data-bundle-picker-colour][value="' + CSS.escape(state.data.fullSet.ids[fi]) + '"]');
        if (fsCheckbox && fsCheckbox.hasAttribute('data-bundle-picker-out-of-stock')) {
          fullSetStillAvailable = false;
          break;
        }
      }
    }
    var match = mode === 'full' && fullSetStillAvailable ? state.data.fullSet : null;
    var fullSetFallbackNote = '';
    if (mode === 'full' && !fullSetStillAvailable) {
      // Per explicit request: don't just refuse the whole Full Set because
      // one colour sold out - offer whatever's still actually in stock
      // instead. The backend already generates a whole range of Combo Set
      // sizes, so "every Full Set colour except the sold-out one(s)" is
      // often already a real, purchasable product - this just looks it up
      // the same way a manually-selected Combo Set would be, rather than
      // leaving the shopper with nothing.
      var colourById = {};
      for (var ci = 0; ci < state.data.colours.length; ci++) colourById[state.data.colours[ci].id] = state.data.colours[ci];
      var availableIds = [];
      var outOfStockNames = [];
      for (var fj = 0; fj < state.data.fullSet.ids.length; fj++) {
        var id = state.data.fullSet.ids[fj];
        var cb = root.querySelector('[data-bundle-picker-colour][value="' + CSS.escape(id) + '"]');
        if (cb && cb.hasAttribute('data-bundle-picker-out-of-stock')) {
          outOfStockNames.push(colourById[id] ? colourById[id].colour : 'One colour');
        } else {
          availableIds.push(id);
        }
      }
      var minCountForFallback = state.data.comboSetMinCount || 3;
      var fallbackMatch = availableIds.length >= minCountForFallback
        ? state.index[availableIds.slice().sort().join('|')]
        : null;
      if (fallbackMatch) {
        match = fallbackMatch;
        fullSetFallbackNote =
          '<p class="bundle-picker__fallback-note">' +
          outOfStockNames.join(', ') + (outOfStockNames.length > 1 ? ' are' : ' is') +
          ' currently out of stock - here’s a set with the other ' + availableIds.length + ' colours instead.</p>';
      } else {
        // No pre-built product exactly matches the reduced colour list (this
        // store's Combo Sets only ever come in one fixed size) - fall back
        // to Mixed Bulk instead of a dead end, since it already computes a
        // real, correct discount across however many colours are selected
        // (see updateBundlePickerBulkSummary), with 1 of each available
        // colour pre-filled so it's still a one-click action, not a manual
        // re-entry of everything Full Set mode already had selected.
        var availableVariantIds = [];
        for (var vi = 0; vi < availableIds.length; vi++) {
          if (colourById[availableIds[vi]]) availableVariantIds.push(colourById[availableIds[vi]].variantId);
        }
        resultEl.hidden = false;
        resultEl.innerHTML =
          '<p class="bundle-picker__fallback-note">' +
          outOfStockNames.join(', ') + (outOfStockNames.length > 1 ? ' are' : ' is') +
          ' currently out of stock, so the Full Set isn’t available as-is.</p>' +
          getBulkPricePreviewHtml(root, availableVariantIds) +
          '<div class="bundle-picker__action-row">' +
          '<button type="button" class="button button--primary" data-bundle-picker-fill-bulk data-bundle-picker-fill-variant-ids="' +
          availableVariantIds.join(',') +
          '">Buy the other ' + availableIds.length + ' colours instead (bulk pricing)</button>' +
          '</div>';
        return;
      }
    }
    if (!match) {
      // comboSetMinCount comes from the backend's resolved Category/Sub
      // Category/Design settings (see bundleSync.ts's resolveBundleSettings)
      // - falls back to 3 for a picker payload synced before this field
      // existed. Below that many selections there's no possible Combo Set
      // to look up at all (the smallest one is exactly this size), so
      // skipping the lookup entirely there is just an optimization, not a
      // correctness requirement - state.index simply wouldn't have a match.
      var minCount = state.data.comboSetMinCount || 3;
      match = selected.length >= minCount ? state.index[selected.slice().sort().join('|')] : null;
    }

    // Per explicit request ("users click suppose 4 to infinity... not show
    // anything here, so move mixed bulk"): Combo Set mode only ever has one
    // fixed size (comboSetMaxCount) - checking more colours than that has no
    // possible Combo Set product to land on, which previously just showed
    // nothing at all. Offer the same "switch to Mixed Bulk, pre-filled"
    // fallback already used above for a Full Set short a sold-out colour,
    // instead of a dead end.
    if (mode === 'combo' && !match && selected.length > (state.data.comboSetMaxCount || state.data.comboSetMinCount || 3)) {
      var comboOverflowVariantIds = [];
      for (var oi = 0; oi < selected.length; oi++) {
        for (var oj = 0; oj < state.data.colours.length; oj++) {
          if (state.data.colours[oj].id === selected[oi]) {
            comboOverflowVariantIds.push(state.data.colours[oj].variantId);
            break;
          }
        }
      }
      resultEl.hidden = false;
      resultEl.innerHTML =
        '<p class="bundle-picker__fallback-note">Combo Sets only come in groups of ' +
        (state.data.comboSetMaxCount || state.data.comboSetMinCount || 3) +
        ' colours, so ' + selected.length + ' selected colours can’t make one Combo Set - here’s bulk pricing for all ' +
        selected.length + ' instead.</p>' +
        getBulkPricePreviewHtml(root, comboOverflowVariantIds) +
        '<div class="bundle-picker__action-row">' +
        '<button type="button" class="button button--primary" data-bundle-picker-fill-bulk data-bundle-picker-fill-variant-ids="' +
        comboOverflowVariantIds.join(',') +
        '">Buy these ' + selected.length + ' colours instead (bulk pricing)</button>' +
        '</div>';
      return;
    }

    // The hint text (data-bundle-picker-hint) stays visible regardless of
    // match state - per explicit request, it's a standing instruction, not
    // a transient message that should disappear once satisfied.
    if (match) {
      var label = match === state.data.fullSet ? 'Full Set' : 'Combo Set';

      // match.price/compareAtPrice come from the backend as plain rupee
      // decimals (e.g. 1950.00) - formatMoney expects paise (Shopify's own
      // convention, matched here to the same data-unit-price attribute
      // used by the quantity total-price calc above), hence *100.
      var priceHtml =
        '<span class="price price--highlight"><span class="money">' + formatMoney(match.price * 100) + '</span></span>';
      if (match.compareAtPrice > match.price) {
        priceHtml +=
          '<span class="price price--compare"><span class="money">' +
          formatMoney(match.compareAtPrice * 100) +
          '</span></span>';
      }

      var savingsHtml = '';
      var savings = match.compareAtPrice - match.price;
      if (savings > 0) {
        savingsHtml =
          '<p class="product-meta__savings-badge">' +
          '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20.59 13.41 11 3.83A2 2 0 0 0 9.59 3.24L3.24 9.59A2 2 0 0 0 3.83 11l9.58 9.59a2 2 0 0 0 2.83 0l4.35-4.35a2 2 0 0 0 0-2.83Z"></path><circle cx="7.5" cy="7.5" r="1.5" fill="currentColor" stroke="none"></circle></svg>' +
          'You saved ' +
          formatMoney(savings * 100) +
          '</p>';
      }

      // Per explicit request: same "N sarees x price = subtotal" math
      // already shown for Single mode's own Total Price, now also here
      // for Combo/Full Set - "how many saree total price discount price"
      // - purely a display addition reading match.ids.length/price/
      // compareAtPrice, which priceHtml/savingsHtml above already use
      // as-is; nothing about what's charged changes, and none of the
      // existing price/savings/action-row markup below is touched.
      var breakdownHtml = '';
      var sareeCount = match.ids ? match.ids.length : 0;
      if (sareeCount > 0 && match.compareAtPrice > 0) {
        var perPieceCents = (match.compareAtPrice * 100) / sareeCount;
        breakdownHtml =
          '<p class="product-form__price-breakdown text--small">' +
          sareeCount + ' sarees × ' + formatMoney(perPieceCents) + ' = ' + formatMoney(match.compareAtPrice * 100) +
          '</p>';
      }

      resultEl.hidden = false;
      resultEl.innerHTML =
        fullSetFallbackNote +
        breakdownHtml +
        '<div class="product-form__price-info price-list">' + priceHtml + '</div>' +
        savingsHtml +
        '<div class="bundle-picker__action-row">' +
        '<a href="/products/' + match.handle + '" class="button bundle-picker__view-set-link" data-bundle-picker-view-handle="' +
        match.handle +
        '">View this ' + label + '</a>' +
        '<button type="button" class="button button--primary" data-bundle-picker-add-to-cart data-bundle-picker-handle="' +
        match.handle +
        '">Add to cart</button>' +
        '<button type="button" class="button button--secondary" data-bundle-picker-checkout data-bundle-picker-handle="' +
        match.handle +
        '">Checkout</button>' +
        '</div>';
    } else {
      resultEl.hidden = true;
      resultEl.innerHTML = '';
    }
  }

  document.addEventListener('change', function (event) {
    if (!event.target.matches || !event.target.matches('[data-bundle-picker-colour]')) return;
    var root = event.target.closest('[data-bundle-picker]');
    if (!root) return;

    // Single mode wants radio-like exclusivity out of these checkboxes
    // (only ever one saree at a time) without actually being real radio
    // inputs - checking one un-checks every other one still checked.
    if (event.target.checked && root.getAttribute('data-bundle-picker-mode') === 'single') {
      var checkboxes = root.querySelectorAll('[data-bundle-picker-colour]');
      for (var i = 0; i < checkboxes.length; i++) {
        if (checkboxes[i] !== event.target) checkboxes[i].checked = false;
      }
    }

    updateBundlePickerResult(root);
  });

  /*
    Switches between "Single" (any one colour, links straight to that
    saree's own page), "Combo Set" (pick some colours yourself, within the
    Sub Category's configured range), and "Full Set" (every colour, always
    - nothing to individually choose) modes. Full Set mode force-checks
    and disables every checkbox instead of asking the shopper to manually
    tick each one, since ticking anything less wouldn't be a Full Set at
    all; switching to Single or Combo Set mode clears the selection rather
    than leaving it full (a full-size selection may not even be a valid
    Combo Set - see computeBundleTargets's comboSetMaxCount cap - and
    Single only ever wants exactly one).
  */
  function setBundlePickerMode(root, mode) {
    root.setAttribute('data-bundle-picker-mode', mode);

    var hintEl = root.querySelector('[data-bundle-picker-hint]');
    if (hintEl) {
      var hintAttrMap = { single: 'data-hint-single', full: 'data-hint-full', bulk: 'data-hint-bulk' };
      var hintAttr = hintAttrMap[mode] || 'data-hint-combo';
      // data-hint-full can now legitimately be an empty string (Full Set's
      // "needs N+ colours" message was removed entirely for that case) -
      // `if (hintText)` treated an empty string as "nothing to update",
      // leaving whatever text the PREVIOUS mode had left behind instead of
      // clearing it - confirmed live (switching Bulk -> Full Set left
      // Bulk's own hint text showing under the Full Set radio). null (the
      // attribute never existing at all) is the only case with nothing to
      // set here; an empty string is a valid "clear it" value.
      var hintText = hintEl.getAttribute(hintAttr);
      if (hintText !== null) hintEl.textContent = hintText;
    }

    // A checkbox marked data-bundle-picker-out-of-stock (see
    // bundle-picker.liquid) stays disabled and unchecked no matter which
    // mode it switches to or from - it can never actually be added to any
    // Single/Combo/Full Set, so Full Set's own force-check-everything
    // behaviour below must not override that.
    var checkboxes = root.querySelectorAll('[data-bundle-picker-colour]');
    for (var i = 0; i < checkboxes.length; i++) {
      if (checkboxes[i].hasAttribute('data-bundle-picker-out-of-stock')) continue;
      checkboxes[i].checked = mode === 'full';
      checkboxes[i].disabled = mode === 'full';
    }

    updateBundlePickerResult(root);

    // Per explicit request: switching modes (Single/Combo Set/Full Set/
    // Mixed Bulk, any direction) resets the native "Quantity"/"Total
    // Price" box below back to a clean baseline (1, full price, no
    // savings) instead of leaving whatever number was typed while
    // exploring a different mode sitting there looking like it still
    // applies to whatever's now selected - confirmed live ("once go to
    // comboset or fullset or mixed any radio button change... again
    // start to old"). That native box belongs to the single product's
    // own Add to Cart, entirely independent of this picker's mode, but
    // visually sits right below it, so a stale leftover number there
    // reads as if it were part of whatever's currently picked.
    var productBlockList = root.closest('.product-block-list');
    if (productBlockList) {
      var nativeQtyInput = productBlockList.querySelector('.quantity-selector--product .quantity-selector__value');
      if (nativeQtyInput) {
        nativeQtyInput.value = 1;
        updateTotalPrice(productBlockList);
      }
    }
  }

  document.addEventListener('change', function (event) {
    if (!event.target.matches || !event.target.matches('[data-bundle-picker-mode-input]')) return;
    var root = event.target.closest('[data-bundle-picker]');
    if (root) setBundlePickerMode(root, event.target.value);
  });

  // "Buy the other N colours instead" button from the Full Set fallback
  // above - switches to Mixed Bulk and pre-fills 1 of each still-available
  // colour, reusing the exact same mode-switch/qty-input event plumbing a
  // shopper's own manual clicks would trigger, rather than duplicating
  // that logic here.
  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('[data-bundle-picker-fill-bulk]');
    if (!button) return;
    var root = button.closest('[data-bundle-picker]');
    if (!root) return;

    var bulkRadio = root.querySelector('[data-bundle-picker-mode-input][value="bulk"]');
    if (bulkRadio) {
      bulkRadio.checked = true;
      bulkRadio.dispatchEvent(new Event('change', { bubbles: true }));
    }

    var variantIds = (button.getAttribute('data-bundle-picker-fill-variant-ids') || '').split(',').filter(Boolean);
    for (var i = 0; i < variantIds.length; i++) {
      var input = root.querySelector('[data-bundle-picker-bulk-qty][data-variant-id="' + CSS.escape(variantIds[i]) + '"]');
      if (input && !input.disabled) {
        input.value = '1';
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }
  });

  /*
    Mixed Bulk mode's running "Total: N sarees · Rs. X" line, per explicit
    request ("total count is needed") - recomputed from every
    [data-bundle-picker-bulk-qty] input's current value and its own
    data-unit-price (set in bundle-picker.liquid from the same per-colour
    price already shown in that input's table row), so it always reflects
    exactly what "Add selected to cart" below is about to send. Also folds
    in each row's own data-unit-savings (that colour's real compare-at
    price minus its selling price, looked up from its own product page,
    not any bundle discount) plus the 10+-quantity discount above into a
    single "You saved Rs. X" line - per explicit request ("you saved
    amount show here please") - shown only once there's actually a
    positive amount to report.

    Per explicit follow-up request, the 10+ discount here triggers off the
    TOTAL quantity across every colour in this table combined, not each
    colour's own quantity individually (confirmed live: 3+4+5 = 12 sarees
    across three different colours showed no discount at all under the
    old per-colour rule, which is not what was wanted here - Single mode
    on an individual product page is unaffected, since there's only ever
    one colour in play there, so "per colour" and "per order" are the same
    thing for that path).

    Each row resolves its OWN discount from its OWN data-bulk-tiers (the
    same real per-colour tier list Single mode already uses), not one
    shared tiers list for the whole table - confirmed live ("single mixed
    discount not override"): a Category/Sub Category/Design-specific
    override rule (see Settings > Quantity Discount) only ever reaches a
    SUBSET of the colours in a Sub Category (whichever Design it targets),
    so using one shared config for every row either applied the wrong
    colours' discount to colours it was never meant for, or - worse -
    silently skipped a colour's own correctly-lower override threshold
    entirely whenever the shared/flat default's threshold hadn't been
    reached yet, even though totalCount had already passed THAT colour's
    real one. data-bulk-tiers is "[]" for a colour the sync hasn't reached
    yet (feature just enabled, or disabled outright), in which case this
    falls back to the shared tiers exactly as before - never a worse
    experience than this used to be, just correct per-colour where real
    data already exists.
  */
  function updateBundlePickerBulkSummary(root) {
    var summaryEl = root.querySelector('[data-bundle-picker-bulk-summary]');
    if (!summaryEl) return;
    var inputs = root.querySelectorAll('[data-bundle-picker-bulk-qty]');
    var totalCount = 0;
    for (var i = 0; i < inputs.length; i++) {
      var qty = parseInt(inputs[i].value, 10);
      if (qty > 0) totalCount += qty;
    }
    var fallbackTiers = getQuantityDiscountTiers();

    var totalPrice = 0;
    var totalSavings = 0;
    for (var i = 0; i < inputs.length; i++) {
      var qty = parseInt(inputs[i].value, 10);
      if (!(qty > 0)) continue;
      var unitPrice = parseFloat(inputs[i].getAttribute('data-unit-price')) || 0;
      var rowBulkTiers = parseBulkTiers(inputs[i].getAttribute('data-bulk-tiers'));
      var rowTier = rowBulkTiers.length ? resolveBulkTierForQty(rowBulkTiers, totalCount) : null;
      // This row's own data-bulk-tiers comes from combo_picker.data (bundleSync.ts),
      // whose tier entries only ever carry {minQuantity, variantId} - no price field
      // (unlike product-info.liquid's single-mode data-bulk-tiers, which comes from
      // the separate quantity_discount.bulk_tiers metafield and always has one).
      // Confirmed live ("why showing Nan"): parseFloat(rowTier.price) on a tier with
      // no price field is NaN, which silently poisoned the whole table's total.
      // Fall back to the generic bundle-picker discount table whenever this row's
      // own tier can't actually price itself, same as when it has no tiers at all.
      var rowTierPrice = rowTier ? parseFloat(rowTier.price) : NaN;
      if (isNaN(rowTierPrice)) rowTier = resolveQuantityDiscountTier(fallbackTiers, totalCount);
      var quantityDiscount = rowTier ? (!isNaN(rowTierPrice) ? unitPrice - rowTierPrice : rowTier.perItem) : 0;
      var effectiveUnitPrice = Math.max(0, unitPrice - quantityDiscount);
      totalPrice += qty * effectiveUnitPrice;
      totalSavings += qty * ((parseFloat(inputs[i].getAttribute('data-unit-savings')) || 0) + quantityDiscount);
    }
    var html =
      'Total: ' + totalCount + (totalCount === 1 ? ' saree' : ' sarees') + ' &middot; <span class="money">' + formatMoney(totalPrice * 100) + '</span>';
    if (totalSavings > 0) {
      html += '<br><span class="bundle-picker__bulk-savings">You saved <span class="money">' + formatMoney(totalSavings * 100) + '</span></span>';
    }
    summaryEl.innerHTML = html;
  }

  // Same reasoning as clampQuantityInput on the main product form - typing
  // (not just the +/- buttons) needs the same real-stock ceiling, per
  // explicit request ("only 10 availables but why enter 100" - confirmed
  // for the main quantity box, and this table's own text input had no
  // ceiling at all even before that). data-max-quantity is that colour's
  // own real stock (bundle-picker.liquid's bulk_quantity), separate from
  // the main form's regular-variant lookup since each row here is already
  // scoped to its own colour's own variant.
  function clampBulkQtyInput(input) {
    var max = parseInt(input.getAttribute('data-max-quantity'), 10);
    if (!(max >= 0)) return;
    // Per explicit request: this colour's real stock is shared with its
    // Regular/Bulk variants elsewhere on the page and any Combo/Full Set
    // that includes it - subtract whatever's already in the cart for
    // either of this row's own variant ids, AND whatever a Combo/Full
    // Set already in the cart is using of this same colour, before
    // treating the rest as available in this row too.
    var variantId = input.getAttribute('data-variant-id');
    var bulkTiersForClamp = parseBulkTiers(input.getAttribute('data-bulk-tiers'));
    var alreadyInCart = getCachedCartQuantity(variantId);
    for (var bi = 0; bi < bulkTiersForClamp.length; bi++) {
      alreadyInCart += getCachedCartQuantity(String(bulkTiersForClamp[bi].variantId));
    }
    var root = input.closest('[data-bundle-picker]');
    var state = root ? getBundlePickerState(root) : null;
    if (state) {
      var colour = state.data.colours.filter(function (c) { return c.variantId === variantId; })[0];
      if (colour) alreadyInCart += getCachedComboFullSetContribution(colour.id, state);
    }
    max = Math.max(0, max - alreadyInCart);
    var value = parseInt(input.value, 10) || 0;
    if (value > max) input.value = max;
  }

  document.addEventListener('input', function (event) {
    if (!event.target.matches || !event.target.matches('[data-bundle-picker-bulk-qty]')) return;
    clampBulkQtyInput(event.target);
    var root = event.target.closest('[data-bundle-picker]');
    if (root) updateBundlePickerBulkSummary(root);
  });

  // The same quantity-selector--product markup/classes used by the main
  // product form's own +/- buttons (see product-info.liquid), but wired up
  // independently here rather than reusing theme.js's private component -
  // that component only ever targets the single quantity input inside its
  // own product form, not an arbitrary number of per-row inputs in a table.
  document.addEventListener('click', function (event) {
    if (!event.target.closest) return;
    var decreaseButton = event.target.closest('[data-bundle-picker-bulk-qty-decrease]');
    var increaseButton = event.target.closest('[data-bundle-picker-bulk-qty-increase]');
    var button = decreaseButton || increaseButton;
    if (!button || button.disabled) return;

    var wrapper = button.closest('.quantity-selector');
    var input = wrapper ? wrapper.querySelector('[data-bundle-picker-bulk-qty]') : null;
    if (!input) return;

    var qty = parseInt(input.value, 10);
    if (isNaN(qty)) qty = 0;
    qty = decreaseButton ? Math.max(0, qty - 1) : qty + 1;
    input.value = qty;
    clampBulkQtyInput(input);

    var root = input.closest('[data-bundle-picker]');
    if (root) updateBundlePickerBulkSummary(root);
  });

  /*
    Mixed Bulk mode's own "Add selected to cart" button - independent of
    every Single/Combo/Full Set lookup above, it just reads whatever
    quantity was typed against each colour's own variant id and adds them
    all to cart together in one Ajax Cart API call, per explicit request
    ("this is each colour select give the quantity add it ... whatever
    add quantity with any saree"). A disabled (out-of-stock) input is
    skipped even though its value should always be 0 anyway - defense in
    depth against a stale/tampered DOM value.
  */
  function addBundlePickerBulkSelection(root, button, redirectTo) {
    var inputs = root.querySelectorAll('[data-bundle-picker-bulk-qty]');

    // Per explicit follow-up request: the 10+ discount triggers off the
    // TOTAL quantity across every colour combined (see
    // updateBundlePickerBulkSummary's own comment for why) - computed once
    // up front so every row below resolves against the exact same count
    // the "Total"/"You saved" summary already showed.
    var totalQty = 0;
    for (var i = 0; i < inputs.length; i++) {
      if (inputs[i].disabled) continue;
      var rowQty = parseInt(inputs[i].value, 10);
      if (rowQty > 0) totalQty += rowQty;
    }

    var items = [];
    for (var i = 0; i < inputs.length; i++) {
      if (inputs[i].disabled) continue;
      var qty = parseInt(inputs[i].value, 10);
      if (!(qty > 0)) continue;

      // Per explicit request: N+ combined has to actually charge less at
      // checkout, not just show a "You saved" preview - submit each
      // colour's real cheaper "Bulk N+ qty" variant for whichever tier the
      // combined total has reached, instead of the regular one. Resolved
      // directly from THIS row's own data-bulk-tiers against totalQty, not
      // gated behind a single shared tiers list first - confirmed live
      // ("single mixed discount not override"): a shared gate meant a
      // colour whose own Category/Sub Category/Design override rule has a
      // LOWER threshold than the flat/shared default never got its
      // discount applied at all once totalQty sat between the two
      // thresholds, since the shared gate alone decided "no discount yet"
      // for every row regardless of what each row's own real data said.
      // data-bulk-tiers is "[]" if this colour has no bulk counterparts
      // yet, in which case this always falls back to the regular variant,
      // same as before this existed.
      var bulkTiersForSubmit = parseBulkTiers(inputs[i].getAttribute('data-bulk-tiers'));
      var bulkVariantIdForSubmit = resolveBulkVariantIdForQty(bulkTiersForSubmit, totalQty);
      var variantId = bulkVariantIdForSubmit || inputs[i].getAttribute('data-variant-id');
      if (variantId) {
        items.push({ id: variantId, quantity: qty });
      }
    }

    if (items.length === 0) {
      showThemeAlert('Enter a quantity for at least one colour first.');
      return;
    }

    button.disabled = true;
    var originalText = button.textContent;
    button.textContent = redirectTo === '/checkout' ? 'Redirecting...' : 'Adding...';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items }),
    })
      .then(function (response) {
        if (!response.ok) return response.json().then(function (data) { throw new Error(data.description || 'Could not add to cart.'); });
        return response.json();
      })
      .then(function () {
        if (redirectTo === '/checkout') {
          window.location.href = redirectTo;
          return;
        }
        // Per explicit request: Mixed Bulk's own "Add to cart" should stay
        // on this page too (same as the Combo/Full Set buttons) and just
        // open the mini-cart drawer - reusing theme.js's own
        // "product:added" event, same as addBundlePickerMatchToCart above.
        for (var i = 0; i < inputs.length; i++) {
          inputs[i].value = 0;
        }
        updateBundlePickerBulkSummary(root);
        document.body.dispatchEvent(new CustomEvent('product:added', { bubbles: true, detail: { quantity: totalQty } }));
        button.disabled = false;
        button.textContent = originalText;
      })
      .catch(function (error) {
        showThemeAlert(error.message || 'Could not add to cart.');
        button.disabled = false;
        button.textContent = originalText;
      });
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('[data-bundle-picker-bulk-add], [data-bundle-picker-bulk-checkout]');
    if (!button) return;
    var root = button.closest('[data-bundle-picker]');
    if (!root) return;
    var redirectTo = button.hasAttribute('data-bundle-picker-bulk-checkout') ? '/checkout' : '/cart';
    addBundlePickerBulkSelection(root, button, redirectTo);
  });

  /*
    Per explicit request: a Combo/Full Set can oversell one of its own
    included colours if that colour is already used up elsewhere in the
    cart (a regular/Bulk purchase of it, or another Combo/Full Set that
    also includes it) - confirmed live repeatedly ("already 10 inventory
    count is reached why fullset is there", "comboset have already 1 have
    gray ... how 11 is show", and again from a collection GRID card's own
    "Add to cart" clicked 4 times in a row with zero warning - "keep on
    adding while add to cart it should check inventory"). Real per-colour
    stock numbers come from [data-bundle-picker-colours-json] +
    data-bundle-composition-ids, both embedded on the SAME element -
    snippets/bundle-picker.liquid's is_bundle_product branch does this on
    a bundle product's own page, and snippets/product-card.liquid does
    the identical thing scoped to each individual grid/search/quick-view
    card - so this one lookup covers every "Add to cart" entry point
    without a network fetch on every click.
  */
  function getBundleProductComposition(root) {
    var coloursJsonEl = root.querySelector('[data-bundle-picker-colours-json]');
    if (!coloursJsonEl) return null;
    var colours;
    try {
      colours = JSON.parse(coloursJsonEl.textContent);
    } catch (e) {
      return null;
    }
    if (!colours || !colours.length) return null;
    var compositionIds = coloursJsonEl.getAttribute('data-bundle-composition-ids');
    if (!compositionIds) return null;
    var ids = compositionIds.split('|');
    var colourById = {};
    for (var j = 0; j < colours.length; j++) colourById[colours[j].id] = colours[j];
    var composition = [];
    for (var k = 0; k < ids.length; k++) {
      if (colourById[ids[k]]) composition.push(colourById[ids[k]]);
    }
    return composition;
  }

  // Companion to [data-bundle-picker-colours-json] (see getBundleProductComposition) -
  // the comboSets/fullSet composition list for every OTHER bundle in this product's
  // own Sub Category, so a bundle product's own page can also detect a DIFFERENT
  // Combo/Full Set already in the cart that consumes the same colour (previously only
  // checkBundlePickerSetStock, used by the collection-grid picker UI, could see this -
  // confirmed live: a Full Set added fine from its own product page even though every
  // unit of one of its included colours was already reserved by a Combo Set sitting in
  // the cart, because this page-level check never knew that other bundle existed).
  function getBundleProductSets(root) {
    var el = root.querySelector('[data-bundle-picker-sets-json]');
    if (!el) return null;
    try {
      return { data: JSON.parse(el.textContent) };
    } catch (e) {
      return null;
    }
  }

  /*
    The reverse direction of checkCompositionStock above: this runs when
    the product being added is a REGULAR saree (not itself a Combo/Full
    Set), to catch a Combo/Full Set already in the cart that reserved some
    of THIS exact colour - confirmed live: a Full Set reserved 1 Beige,
    then Beige's own product page still let 10 more be added directly on
    top of that with zero warning, since nothing ever checked a direct
    purchase against bundles already sitting in the cart. Uses the main
    picker widget's own [data-bundle-picker-json] (rendered on every
    tracked saree's own page/card regardless of bundle status) plus
    data-bundle-picker-own-colour-id to find which colour entry is this
    exact product.
  */
  function getOwnColourStock(root) {
    var pickerEl = root.querySelector('[data-bundle-picker][data-bundle-picker-own-colour-id]');
    if (!pickerEl) return null;
    var colourId = pickerEl.getAttribute('data-bundle-picker-own-colour-id');
    if (!colourId) return null;
    var jsonEl = pickerEl.querySelector('[data-bundle-picker-json]');
    if (!jsonEl) return null;
    var data;
    try {
      data = JSON.parse(jsonEl.textContent);
    } catch (e) {
      return null;
    }
    var colour = null;
    for (var i = 0; i < (data.colours || []).length; i++) {
      if (data.colours[i].id === colourId) {
        colour = data.colours[i];
        break;
      }
    }
    if (!colour || typeof colour.quantity !== 'number') return null;
    return { colour: colour, state: { data: data } };
  }

  function checkOwnColourStock(root, requestedQuantity) {
    var own = getOwnColourStock(root);
    if (!own) return Promise.resolve(null);
    var qty = Math.max(1, Number(requestedQuantity) || 1);
    return getCartItemsForStockCheck()
      .then(function (items) {
        var relevantVariantIds = [String(own.colour.variantId)].concat(collectBulkVariantIds(own.colour));
        var inCart = 0;
        for (var j = 0; j < items.length; j++) {
          if (relevantVariantIds.indexOf(String(items[j].variant_id)) !== -1) {
            inCart += items[j].quantity;
          }
        }
        inCart += computeComboFullSetContribution(own.colour.id, items, own.state);
        if (inCart + qty > own.colour.quantity) {
          return own.colour.colour + ' only has ' + own.colour.quantity + ' in stock, and your cart already has ' + inCart + ' - not enough left to also add ' + qty + ' more.';
        }
        return null;
      })
      .catch(function () { return null; });
  }

  function checkCompositionStock(composition, root) {
    if (!composition || !composition.length) return Promise.resolve(null);
    var setsState = root ? getBundleProductSets(root) : null;
    return getCartItemsForStockCheck()
      .then(function (items) {
        for (var i = 0; i < composition.length; i++) {
          var colour = composition[i];
          if (typeof colour.quantity !== 'number') continue;
          var relevantVariantIds = [String(colour.variantId)].concat(collectBulkVariantIds(colour));
          var inCart = 0;
          for (var j = 0; j < items.length; j++) {
            if (relevantVariantIds.indexOf(String(items[j].variant_id)) !== -1) {
              inCart += items[j].quantity;
            }
          }
          // Per explicit request: another Combo/Full Set already in the
          // cart that also includes this colour "uses up" one of it too -
          // same cross-bundle check checkBundlePickerSetStock already does.
          if (setsState) inCart += computeComboFullSetContribution(colour.id, items, setsState);
          if (inCart + 1 > colour.quantity) {
            return colour.colour + ' only has ' + colour.quantity + ' in stock, and your cart already has ' + inCart + ' - not enough left to also include it in this set.';
          }
        }
        return null;
      })
      .catch(function () { return null; }); // a failed check shouldn't itself block a purchase
  }

  /*
    "Checkout" button next to Add to Cart - adds the currently selected
    variant/quantity straight to the cart (same form data a real Add to
    Cart submit would send, via FormData so it carries quantity, any
    bundle-picker property inputs, etc.) and skips the cart page/drawer
    entirely, landing the customer straight on Shopify's real checkout.
    type="button" (not submit) so it never collides with theme.js's own
    submit-driven Add to Cart handling on the same <form> - this has its
    own independent click handler instead.
  */
  function getFormQuantity(form) {
    if (!form) return 1;
    var input = form.querySelector('[name="quantity"]');
    var value = input ? Number(input.value) : 1;
    return value > 0 ? value : 1;
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('[data-action="checkout-now"]');
    if (!button) return;

    var form = button.closest('form');
    if (!form) return;
    if (form.reportValidity && !form.reportValidity()) return;

    var root = button.closest('.product-block-list') || form;
    var composition = getBundleProductComposition(root);
    var stockCheck = composition
      ? checkCompositionStock(composition, root)
      : checkOwnColourStock(root, getFormQuantity(form));

    button.disabled = true;
    var originalText = button.textContent;
    button.textContent = 'Checking stock...';

    stockCheck.then(function (warning) {
      if (warning) {
        showThemeAlert(warning);
        button.disabled = false;
        button.textContent = originalText;
        return;
      }
      button.textContent = 'Redirecting...';
      fetch('/cart/add.js', {
        method: 'POST',
        body: new FormData(form),
      })
        .then(function (response) {
          if (!response.ok) return response.json().then(function (data) { throw new Error(data.description || 'Could not add to cart.'); });
          window.location.href = '/checkout';
        })
        .catch(function (error) {
          showThemeAlert(error.message || 'Could not add to cart.');
          button.disabled = false;
          button.textContent = originalText;
        });
    });
  });

  /*
    Same stock check as the Checkout button above, but for the NATIVE
    "Add to cart" submit button (theme.js's own AJAX handler) - a capture-
    phase listener so this runs and can block (preventDefault +
    stopImmediatePropagation) BEFORE theme.js's own bubble-phase handler
    ever sees the click, when this product is itself a Combo/Full Set.

    [data-action="add-to-cart"] (not .product-form__add-button, which only
    ever matched the product page's own button) is the one marker theme.js
    itself uses on every native Add to Cart button site-wide - the product
    page, a collection/search grid card, and quick-view's re-rendered copy
    of the product page all carry it, so this now protects all of them the
    same way, not just the standalone product page.
  */
  document.addEventListener(
    'click',
    function (event) {
      var button = event.target.closest && event.target.closest('[data-action="add-to-cart"]');
      if (!button || button.disabled) return;
      // Set by this same listener right before re-clicking below once the
      // check has already passed - without this, that synthetic click
      // would hit this exact listener again and loop forever.
      if (button.dataset.bundleStockChecked) {
        delete button.dataset.bundleStockChecked;
        return;
      }
      var root = button.closest('.product-item, .product-card-wrapper, .product-block-list');
      if (!root) return;
      var composition = getBundleProductComposition(root);
      var form = button.closest('form');
      var ownColourStock = !composition ? getOwnColourStock(root) : null;
      if (!composition && !ownColourStock) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      var originalText = button.textContent;
      button.disabled = true;
      button.textContent = 'Checking stock...';
      var stockCheck = composition
        ? checkCompositionStock(composition, root)
        : checkOwnColourStock(root, getFormQuantity(form));
      stockCheck.then(function (warning) {
        button.disabled = false;
        button.textContent = originalText;
        if (warning) {
          showThemeAlert(warning);
          return;
        }
        button.dataset.bundleStockChecked = 'true';
        button.click();
      });
    },
    true
  );

  /*
    Hovering a colour swatch's thumbnail in the picker shows a bigger
    preview of that same saree floating near it, without navigating
    anywhere or opening any modal - a full quick-view popup on hover
    (this previously fetched and opened one on *click*) fires far too
    eagerly for a hover trigger, popping up on every swatch the cursor
    passes while scanning options. Clicking the thumbnail is left alone
    (falls through to the checkbox <label> it's inside, same as clicking
    anywhere else in that label) instead of being intercepted for this.
  */
  var bundlePickerHoverPreview = null;
  function showBundlePickerHoverPreview(thumb) {
    var src = thumb.getAttribute('data-bundle-picker-preview');
    if (!src) return;
    if (!bundlePickerHoverPreview) {
      bundlePickerHoverPreview = document.createElement('div');
      bundlePickerHoverPreview.className = 'bundle-picker__hover-preview';
      bundlePickerHoverPreview.innerHTML = '<img alt="">';
      document.body.appendChild(bundlePickerHoverPreview);
    }
    bundlePickerHoverPreview.querySelector('img').src = src;
    var rect = thumb.getBoundingClientRect();
    bundlePickerHoverPreview.style.left = rect.left + 'px';
    bundlePickerHoverPreview.style.top = rect.bottom + 8 + 'px';
    bundlePickerHoverPreview.classList.add('is-visible');
  }
  function hideBundlePickerHoverPreview() {
    if (bundlePickerHoverPreview) bundlePickerHoverPreview.classList.remove('is-visible');
  }

  document.addEventListener('mouseover', function (event) {
    var thumb = event.target.closest && event.target.closest('[data-bundle-picker-preview]');
    if (thumb) showBundlePickerHoverPreview(thumb);
  });
  document.addEventListener('mouseout', function (event) {
    var thumb = event.target.closest && event.target.closest('[data-bundle-picker-preview]');
    if (thumb) hideBundlePickerHoverPreview();
  });

  /*
    Per explicit request: "View this saree/Combo Set/Full Set" opens a
    preview right here on the same page instead of navigating away to a
    new one. Reuses the theme's own shared #QuickViewModal shell (styling,
    close button, loader, ESC/backdrop-close all already wired up by
    assets/theme.js's global ModalManager, which listens on document.body
    - see snippets/quick-view-modal.liquid) and the same
    `{product-url}?view=quick-view` endpoint the native "Quick view"
    buttons use, fetched and injected exactly like assets/theme.js's own
    CollectionSection._openQuickView does.

    What's custom here (and why): that native fetch+inject logic is only
    ever bound by a specific already-instantiated section component
    (CollectionSection/CartSection), scoped to elements inside that
    component's own root - the picker isn't inside one of those on an
    individual product page, so nothing would fire there without this.
    Triggering the actual *open* (aria-hidden flip) via a synthetic click
    on a real `[data-action="open-modal"]` button reuses ModalManager's
    own logic for that part, since it IS a global, unscoped delegate -
    only the fetch+inject needs reimplementing, not modal open/close/focus
    handling. Left as a real `<a href>` (not a button) so a shopper with
    JS disabled still gets a working link - the click handler below calls
    preventDefault before the fetch starts, which is what stops the normal
    navigation once JS *is* available.
  */
  function openBundlePickerQuickView(handle) {
    var modal = document.getElementById('QuickViewModal');
    if (!modal || !handle) return;

    modal.classList.add('is-loading');
    fetch('/products/' + handle + '?view=quick-view', { credentials: 'same-origin', method: 'GET' })
      .then(function (response) {
        return response.text();
      })
      .then(function (content) {
        modal.querySelector('.modal__inner').innerHTML = content;
        modal.classList.remove('is-loading');
      })
      .catch(function () {
        modal.classList.remove('is-loading');
      });

    var opener = document.createElement('button');
    opener.type = 'button';
    opener.setAttribute('data-action', 'open-modal');
    opener.setAttribute('aria-controls', 'QuickViewModal');
    opener.style.display = 'none';
    document.body.appendChild(opener);
    opener.click();
    opener.remove();
  }

  /*
    Per explicit follow-up request, Single mode navigates to the selected
    colour's real product page (checking a colour, or clicking "View this
    saree", both trigger this) rather than a popup. A same-page swap was
    tried first and confirmed live to break the theme's own JS-driven
    layout (image gallery/"You May Also Like" carousel positioning never
    recalculates for content injected after page load, since that JS only
    ever initializes once at load) - a real navigation is what actually
    guarantees correct layout every time, since it's just this site's
    already-working product page rendering normally.
  */
  document.addEventListener('click', function (event) {
    var link = event.target.closest && event.target.closest('[data-bundle-picker-view-handle]');
    if (!link) return;
    var root = link.closest('[data-bundle-picker]');
    var mode = root ? root.getAttribute('data-bundle-picker-mode') : null;
    if (mode === 'single') return; // real <a href> already points at the right URL - let it navigate normally.
    event.preventDefault();
    openBundlePickerQuickView(link.getAttribute('data-bundle-picker-view-handle'));
  });

  /*
    "Add to cart"/"Checkout" buttons next to "View this Combo/Full Set" -
    per explicit request, buying the matched set shouldn't require going
    through "View this Set" first. The matched set's real variant id
    isn't in the embedded JSON (comboSets/fullSet only carry
    handle/price/compareAtPrice/ids - see getBundlePickerState), so it's
    looked up on demand via the set product's own public .js endpoint
    (every Combo/Full Set product has exactly one variant, same as
    "Kanchi Cotton Full Set") rather than adding a second field the
    backend sync would have to keep in permanent lockstep with the real
    product. Checkout still redirects straight to Shopify's real
    checkout, same as the product page's own Checkout button - but per
    explicit request, Add to cart should NOT leave this page at all
    ("dont go to main cart. only add in minicart"). Dispatching theme.js's
    own real "product:added" event (bubbling, same shape theme.js's
    native _addToCart dispatches - see theme.js's HeaderSection
    _onProductAdded) reuses its existing re-render + open-the-drawer
    logic instead of reimplementing a mini-cart refresh here.
  */
  function addBundlePickerMatchToCart(handle, button, redirectTo) {
    var originalText = button.textContent;
    button.disabled = true;
    button.textContent = redirectTo === '/checkout' ? 'Redirecting...' : 'Adding...';
    fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('Could not load this set.');
        return response.json();
      })
      .then(function (product) {
        var variant = product.variants && product.variants[0];
        if (!variant) throw new Error('This set has no purchasable variant right now.');
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: variant.id, quantity: 1 }),
        });
      })
      .then(function (response) {
        if (!response.ok) return response.json().then(function (data) { throw new Error(data.description || 'Could not add to cart.'); });
        if (redirectTo === '/checkout') {
          window.location.href = redirectTo;
          return;
        }
        // Dispatched on document.body (not document itself) - theme.js's
        // HeaderSection listens via a Delegate rooted at document.documentElement,
        // which only ever sees events that bubble UP through it from a
        // descendant; an event fired directly on `document` has no ancestor
        // to bubble through and would never reach that listener.
        document.body.dispatchEvent(new CustomEvent('product:added', { bubbles: true, detail: { quantity: 1 } }));
        button.disabled = false;
        button.textContent = originalText;
      })
      .catch(function (error) {
        showThemeAlert(error.message || 'Could not add to cart.');
        button.disabled = false;
        button.textContent = originalText;
      });
  }

  /*
    Per explicit request: a Combo/Full Set is its own separate Shopify
    product with its OWN independently-tracked inventory count - Shopify
    has no native concept of "this bundle's stock = the minimum across
    its N included colours' real stock", so nothing stops a shopper from
    having, say, 10 units of "Magenta Pink" already in their cart (its
    entire real stock, per picker_data.colours[].quantity) and then ALSO
    adding a Full Set that includes one more Magenta Pink - reported live
    ("already 10 inventory count is reached why fullset is there... that
    saree also there in fullset"). This is a best-effort, storefront-side
    safety net specifically for the picker's own Add to cart/Checkout
    buttons: before adding, check every colour the matched set is made of
    against what's already sitting in this shopper's own cart (regular +
    Bulk variant combined, since both draw from the same physical stock)
    plus the 1 this set needs, using the picker's own already-embedded
    colours[].quantity as the real ceiling. This can't catch a conflict
    from *other* shoppers' simultaneous carts (only Shopify's own
    inventory system could do that, and only if the bundle's stock count
    were actually kept in sync with its components - a backend/inventory-
    sync fix, not something fixable in the theme), but it does stop the
    exact scenario reported here.
  */
  function findBundlePickerMatchByHandle(state, handle) {
    var fullSets = allBundlePickerFullSets(state.data);
    for (var f = 0; f < fullSets.length; f++) {
      if (fullSets[f].handle === handle) return fullSets[f];
    }
    for (var i = 0; i < state.data.comboSets.length; i++) {
      if (state.data.comboSets[i].handle === handle) return state.data.comboSets[i];
    }
    return null;
  }

  function checkBundlePickerSetStock(root, handle) {
    var state = getBundlePickerState(root);
    var match = state ? findBundlePickerMatchByHandle(state, handle) : null;
    if (!match || !match.ids) return Promise.resolve(null);

    var colourById = {};
    for (var i = 0; i < state.data.colours.length; i++) {
      colourById[state.data.colours[i].id] = state.data.colours[i];
    }

    return getCartItemsForStockCheck()
      .then(function (items) {
        for (var i = 0; i < match.ids.length; i++) {
          var colour = colourById[match.ids[i]];
          if (!colour || typeof colour.quantity !== 'number') continue;
          var relevantVariantIds = [String(colour.variantId)].concat(collectBulkVariantIds(colour));
          var inCart = 0;
          for (var j = 0; j < items.length; j++) {
            if (relevantVariantIds.indexOf(String(items[j].variant_id)) !== -1) {
              inCart += items[j].quantity;
            }
          }
          // Per explicit request: another Combo/Full Set already in the
          // cart that also includes this colour "uses up" one of it too.
          if (state) inCart += computeComboFullSetContribution(colour.id, items, state);
          if (inCart + 1 > colour.quantity) {
            return colour.colour + ' only has ' + colour.quantity + ' in stock, and your cart already has ' + inCart + ' - not enough left to also include it in this set.';
          }
        }
        return null;
      })
      .catch(function () { return null; }); // a failed check shouldn't itself block a purchase
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('[data-bundle-picker-add-to-cart], [data-bundle-picker-checkout]');
    if (!button) return;
    var handle = button.getAttribute('data-bundle-picker-handle');
    if (!handle) return;
    var root = button.closest('[data-bundle-picker]');
    var redirectTo = button.hasAttribute('data-bundle-picker-checkout') ? '/checkout' : '/cart';

    var originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'Checking stock...';
    checkBundlePickerSetStock(root, handle).then(function (warning) {
      button.disabled = false;
      button.textContent = originalText;
      if (warning) {
        showThemeAlert(warning);
        return;
      }
      addBundlePickerMatchToCart(handle, button, redirectTo);
    });
  });

  /*
    Custom thumbnail-switching for the gallery inside the quick-view popup
    only (scoped to #QuickViewModal, same reasoning as the zoom handler
    below). The native gallery (assets/theme.js's ProductGallery) is only
    ever instantiated for the real product page's own markup at load time
    - the picker's "View this Combo/Full Set" opens its popup via a plain
    fetch+innerHTML (see openBundlePickerQuickView above), which has no way
    to run that private, closure-scoped component against the freshly
    injected content, so its thumbnails otherwise do nothing at all
    (confirmed live: clicking one was a dead click).

    Reimplemented directly instead: `.product-gallery__carousel-item:not
    (.is-selected){display:none}` is a plain CSS rule (confirmed in
    theme.css, not JS-driven), so toggling `.is-selected` to the item
    matching a clicked thumbnail's `data-media-id` is the entire
    mechanism - no slider/transform math needed.
  */
  document.addEventListener('click', function (event) {
    var thumb = event.target.closest && event.target.closest('#QuickViewModal .product-gallery__thumbnail');
    if (!thumb) return;
    event.preventDefault();

    var modal = thumb.closest('#QuickViewModal');
    var mediaId = thumb.getAttribute('data-media-id');

    var thumbs = modal.querySelectorAll('.product-gallery__thumbnail');
    for (var i = 0; i < thumbs.length; i++) {
      thumbs[i].classList.toggle('is-nav-selected', thumbs[i] === thumb);
    }

    var items = modal.querySelectorAll('.product-gallery__carousel-item');
    for (var j = 0; j < items.length; j++) {
      items[j].classList.toggle('is-selected', items[j].getAttribute('data-media-id') === mediaId);
    }
  });

  /*
    Custom click-to-zoom for images inside the quick-view popup only
    (scoped to #QuickViewModal so this never touches the real product
    page's own gallery, which this theme intentionally ships with zoom
    turned off - `enableImageZoom: false` in sections/main-product.liquid's
    section settings). The quick-view popup itself still opens normally
    from a product card's own "Quick view" button (assets/theme.js's
    native CollectionSection._openQuickView) - only the bundle picker's
    swatch preview above was changed to hover instead of opening this.
  */
  var bundlePickerZoomOverlay = null;
  function openBundlePickerImageZoom(src) {
    if (!bundlePickerZoomOverlay) {
      bundlePickerZoomOverlay = document.createElement('div');
      bundlePickerZoomOverlay.className = 'bundle-picker-zoom-overlay';
      bundlePickerZoomOverlay.innerHTML = '<img class="bundle-picker-zoom-overlay__image" alt="">';
      document.body.appendChild(bundlePickerZoomOverlay);
      bundlePickerZoomOverlay.addEventListener('click', function () {
        bundlePickerZoomOverlay.classList.remove('is-visible');
      });
    }
    bundlePickerZoomOverlay.querySelector('img').src = src;
    bundlePickerZoomOverlay.classList.add('is-visible');
  }

  document.addEventListener('click', function (event) {
    var image = event.target.closest && event.target.closest('#QuickViewModal .product-gallery__image');
    if (!image) return;
    event.preventDefault();
    openBundlePickerImageZoom(image.currentSrc || image.src);
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && bundlePickerZoomOverlay && bundlePickerZoomOverlay.classList.contains('is-visible')) {
      bundlePickerZoomOverlay.classList.remove('is-visible');
    }
  });

  function showCopied(button) {
    var original = button.getAttribute('aria-label');
    button.setAttribute('aria-label', 'Link copied!');

    // aria-label alone is announced to screen readers but never rendered on
    // screen, so a sighted user clicking this saw no feedback at all and
    // reasonably assumed the button was broken - show the same confirmation
    // visibly too.
    var tooltip = button.parentElement && button.parentElement.querySelector('[data-copy-link-tooltip]');
    if (tooltip) tooltip.classList.add('is-visible');

    setTimeout(function () {
      button.setAttribute('aria-label', original);
      if (tooltip) tooltip.classList.remove('is-visible');
    }, 2000);
  }

  function fallbackCopy(url, button) {
    var textarea = document.createElement('textarea');
    textarea.value = url;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    try {
      document.execCommand('copy');
      showCopied(button);
    } catch (e) {
      window.prompt('Copy this link:', url);
    }
    document.body.removeChild(textarea);
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('[data-action="copy-product-link"]');
    if (!button) return;
    var url = button.getAttribute('data-copy-url');
    if (!url) return;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(
        function () { showCopied(button); },
        function () { fallbackCopy(url, button); }
      );
    } else {
      fallbackCopy(url, button);
    }
  });

  // Per explicit request: on an individual saree's own page, the picker's
  // Single mode already renders with *this* saree's own checkbox
  // pre-checked (see snippets/bundle-picker.liquid's current_colour_prefix)
  // - this script tag runs `defer`, so the DOM is already parsed by now,
  // but nothing has computed a result for that pre-checked box yet
  // (updateBundlePickerResult only ever runs from a 'change' event) until
  // this runs it once for whatever's already checked on load. The skip
  // flag stops that first run from treating its own pre-checked box as a
  // brand new selection and auto-navigating to the page it's already on.
  var initialPickers = document.querySelectorAll('[data-bundle-picker]');
  for (var initI = 0; initI < initialPickers.length; initI++) {
    updateBundlePickerBulkSummary(initialPickers[initI]);
    initialPickers[initI]._bundlePickerSkipAutoNavigate = true;
    updateBundlePickerResult(initialPickers[initI]);
  }

  /*
    Per explicit report ("You May Also Like Quick view not working, not
    showing"): theme.js's native [data-secondary-action="open-quick-view"]
    handler (_openQuickView) only exists on CartSection and
    CollectionSection - the two classes that get instantiated on the cart
    page and on collection/search pages respectively. A plain PRODUCT page
    instantiates neither (only ProductSection, for the main product form),
    so its own "You May Also Like" carousel (snippets/related-products.liquid,
    reusing the exact same product-card markup/button) has this button
    present in the DOM with no listener ever bound to it at all - confirmed
    live: the modal opens (a separate, generic aria-controls-based toggle
    handles that part everywhere already) but stays completely blank,
    because only _openQuickView's own fetch+inject ever populates
    .modal__inner, and nothing calls it on that page type.

    This fallback redoes just that fetch+inject step (not ProductSection's
    own instantiation - it's privately scoped inside theme.js's closure,
    not reachable from this separate file - so some of a true collection-
    page quick view's extra interactivity, like swapping the gallery image
    per swatch, won't be wired up here; Add to cart inside the injected
    markup still works regardless, via the universal
    [data-action="add-to-cart"] listener already set up above). Waits a
    beat first so it never fights the native handler where one DOES exist
    (cart page, collection pages) - if that's already filled .modal__inner
    by the time this checks, this does nothing.
  */
  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('[data-secondary-action="open-quick-view"]');
    if (!button) return;
    var modal = document.getElementById(button.getAttribute('aria-controls'));
    var productUrl = button.getAttribute('data-product-url');
    if (!modal || !productUrl) return;
    setTimeout(function () {
      var inner = modal.querySelector('.modal__inner');
      if (!inner || inner.innerHTML.trim() !== '') return;
      modal.classList.add('is-loading');
      fetch(productUrl + '?view=quick-view', { credentials: 'same-origin', method: 'GET' })
        .then(function (response) { return response.text(); })
        .then(function (content) {
          inner.innerHTML = content;
          modal.classList.remove('is-loading');
        })
        .catch(function () {
          modal.classList.remove('is-loading');
        });
    }, 80);
  });

  /*
    Per final explicit request ("not show comboset and full set here"
    after an earlier "need today morning code for search"): the search
    dropdown's Products section excludes Combo/Full Set (predictive-
    search.liquid), but Shopify's predictive search is hard-capped at 10
    raw matches (confirmed - requesting more doesn't raise it), and some
    designs now have more Combo Set colour-permutation products than real
    single sarees - a broad query can end up with all 10 raw slots being
    bundles, leaving nothing in Products even though real, in-stock single
    sarees exist and match.

    This watches the dropdown for exactly that empty-Products state and
    fills it in by fetching real products directly from the full /search
    page instead (section_id=main-search - Shopify's Section Rendering
    API, returns just that section's HTML, not the whole page) - the full
    page isn't capped at 10 the way predictive search is, and already
    excludes Combo/Full Set itself (same exclusion, so this never
    reintroduces them). Falls back silently (does nothing) if the full
    page also has nothing - matches this theme's existing pattern of
    "a failed check shouldn't itself block/break anything" elsewhere
    (see checkBundlePickerSetStock).
  */
  (function () {
    var resultsInner = document.querySelector('.search-bar__results-inner');
    var searchInput = document.querySelector('.search-bar__input');
    if (!resultsInner || !searchInput) return;
    var lastFallbackQuery = null;

    function hasCategory(name) {
      var headings = resultsInner.querySelectorAll('.search-bar__result-category');
      for (var i = 0; i < headings.length; i++) {
        if (headings[i].textContent.trim() === name) return true;
      }
      return false;
    }

    function buildResultItemHtml(card) {
      var titleEl = card.querySelector('.product-item__title, .product-card__title');
      var linkEl = card.querySelector('a[href]');
      var imgEl = card.querySelector('img');
      var priceEl = card.querySelector('.product-item__price-list .price, .product-card__price');
      if (!titleEl || !linkEl) return '';
      return (
        '<a href="' + linkEl.getAttribute('href') + '" class="search-bar__result-item">' +
          '<div class="search-bar__image-container"><div class="aspect-ratio aspect-ratio--square">' +
          (imgEl ? '<img src="' + imgEl.getAttribute('src') + '" alt="" width="60" height="60" loading="lazy">' : '') +
          '</div></div>' +
          '<div class="search-bar__item-info">' +
            '<p class="search-bar__item-title heading">' + titleEl.textContent.trim() + '</p>' +
            '<p class="search-bar__item-price">' + (priceEl ? priceEl.textContent.trim() : '') + '</p>' +
          '</div>' +
        '</a>'
      );
    }

    function fetchSearchCards(searchQuery) {
      return fetch('/search?q=' + encodeURIComponent(searchQuery) + '&type=product&section_id=main-search', { credentials: 'same-origin' })
        .then(function (response) { return response.text(); })
        .then(function (html) {
          return new DOMParser().parseFromString(html, 'text/html').querySelectorAll('.product-item, .product-card-wrapper');
        });
    }

    /*
      The plain /search page can itself come back empty: its first page of
      48 raw matches can be ALL Combo/Full Sets (confirmed live: "sungudi"
      - 443 bundle matches rank ahead of all 73 real Sungudi sarees, so
      page 1 shows nothing). Retry with each word title-qualified plus
      -tag:auto-bundle*, which makes Shopify drop the bundles before
      paginating. The words must be field-qualified: with a bare word,
      Shopify ORs the negation and returns every non-bundle product in the
      store ("sungudi -tag:auto-bundle*" = all 333), whereas
      "title:sungudi -tag:auto-bundle*" = exactly the 73 real Sungudi
      sarees, and a word no title has gives nothing rather than junk.
    */
    function bundleFreeTitleQuery(query) {
      var words = query.replace(/["():]/g, ' ').split(/\s+/).filter(Boolean);
      if (!words.length) return '';
      return words.map(function (word) { return 'title:' + word; }).join(' ') + ' -tag:auto-bundle*';
    }

    function fetchFallbackProducts(query) {
      fetchSearchCards(query)
        .then(function (cards) {
          if (cards.length) return cards;
          var retryQuery = bundleFreeTitleQuery(query);
          return retryQuery ? fetchSearchCards(retryQuery) : cards;
        })
        .then(function (cards) {
          // Bail if the dropdown already moved on to a different/cleared
          // query by the time this slower, uncapped fetch comes back.
          if (searchInput.value.trim() !== query) return;
          if (hasCategory('Products')) return;
          var itemsHtml = '';
          var count = 0;
          for (var i = 0; i < cards.length && count < 5; i++) {
            var itemHtml = buildResultItemHtml(cards[i]);
            if (itemHtml) {
              itemsHtml += itemHtml;
              count++;
            }
          }
          if (!count) return;
          var heading = document.createElement('p');
          heading.className = 'search-bar__result-category';
          heading.textContent = 'Products';
          var wrapper = document.createElement('div');
          wrapper.innerHTML = itemsHtml;
          // Collections (if present) stay last, same position as before.
          var collectionsHeading = null;
          var headings = resultsInner.querySelectorAll('.search-bar__result-category');
          for (var h = 0; h < headings.length; h++) {
            if (headings[h].textContent.trim() === 'Collections') collectionsHeading = headings[h];
          }
          var insertBefore = collectionsHeading || resultsInner.querySelector('.search-bar__view-all') || null;
          if (insertBefore) {
            resultsInner.insertBefore(heading, insertBefore);
            while (wrapper.firstChild) resultsInner.insertBefore(wrapper.firstChild, insertBefore);
          } else {
            resultsInner.appendChild(heading);
            while (wrapper.firstChild) resultsInner.appendChild(wrapper.firstChild);
          }
        })
        .catch(function () {
          // A failed fallback fetch just leaves Products absent - no worse
          // than before this existed, never shows a broken/partial state.
        });
    }

    /*
      Per explicit report ("i need to show the products... already shown
      now not show"): reacting to every single resultsInner mutation
      (DOM-change-driven, with no settle delay) was unreliable in real
      typing - theme.js's own predictive fetch is debounced, so a
      mutation can fire for an OLDER, already-stale query's render while
      searchInput.value has since moved on to a newer one a user typed in
      the meantime (confirmed re-testable: fast typing outpaces each
      fetch/render cycle). Checking `hasCategory('Products')` against
      that stale render while treating it as if it matched the CURRENT
      input could wrongly skip firing the fallback even though the
      query actually on screen has nothing in Products.

      Debouncing on the input's own "input" event instead (not on
      resultsInner's mutations) and re-checking after a short settle
      delay decouples this from theme.js's render timing entirely -  by
      the time the delay elapses, typing has normally paused long enough
      for theme.js's own (similarly short) debounce to have already
      completed, so what's actually rendered reliably matches what's
      currently typed.
    */
    // Per explicit report ("i need to show the products... already shown
    // now not show"): a single check 400ms after typing stops still
    // sometimes landed BEFORE theme.js's own predictive fetch had
    // finished rendering for that exact query (confirmed re-testable:
    // first search of a session failed, an identical second attempt
    // right after succeeded, purely because resultsInner happened to
    // already be settled by then) - and since nothing re-triggers this
    // once the user has stopped typing, that one early check was the
    // only chance it ever got. Polling for up to ~2s instead of a single
    // one-shot check means it keeps waiting for theme.js's render to
    // actually land rather than giving up the instant it's a little slow.
    var debounceTimer = null;
    searchInput.addEventListener('input', function () {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function () {
        var query = searchInput.value.trim();
        if (!query || query === lastFallbackQuery) return;
        var attempts = 0;
        var poll = setInterval(function () {
          attempts++;
          // The query changed again (user kept typing) - let the next
          // input event's own debounce/poll cycle take over instead.
          if (searchInput.value.trim() !== query) {
            clearInterval(poll);
            return;
          }
          // "View all results" counts as rendered too: when every raw match
          // is a Combo/Full Set and nothing else matches (e.g. "sungudi ad"),
          // the dropdown has no heading at all - just that link - and
          // waiting for a heading meant the fallback never ran.
          if (!resultsInner.querySelector('.search-bar__result-category, .search-bar__view-all')) {
            if (attempts >= 13) clearInterval(poll); // ~2s at 150ms, give up quietly
            return;
          }
          clearInterval(poll);
          if (hasCategory('Products')) return;
          lastFallbackQuery = query;
          fetchFallbackProducts(query);
        }, 150);
      }, 400);
    });
    // Reset so clearing the box (or re-typing the exact same query later)
    // can re-trigger the check instead of being silently skipped forever
    // by the `query === lastFallbackQuery` guard above.
    searchInput.addEventListener('input', function () {
      if (!searchInput.value.trim()) lastFallbackQuery = null;
    });
  })();

  /*
    Per explicit request ("hower this photo... need to show zoom some level
    user see this saree image"): the product gallery (snippets/product-
    info.liquid) had no zoom at all - just a plain Flickity carousel, no
    click-to-zoom or hover-magnify wired up. Desktop-only (matches the CSS
    gating in component-product-gallery.css): mousemove over the CURRENTLY
    VISIBLE slide (.is-selected - the others are display:none, so only this
    one can ever receive the event) scales the image up and sets its
    transform-origin to the cursor position, giving a cursor-following
    magnify effect; mouseleave resets it. Delegated on document rather than
    bound per-image, so it also covers the Quick view popup's copy of this
    exact markup, and keeps working across Flickity re-rendering slides on
    swipe/thumbnail-click without needing to re-bind anything.
  */
  if (window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
    var GALLERY_ZOOM_SCALE = 2.2;
    // Per a performance audit: mousemove can fire far faster than the
    // display can paint (a high-polling-rate mouse/trackpad easily exceeds
    // 60-120 events/sec), and every one of them was doing a querySelector +
    // getBoundingClientRect + two style writes - wasted work when several
    // events land within the same frame. Coalescing to one requestAnimationFrame
    // per frame (using whichever event was most recent when it fires) caps
    // the real work to the display's own refresh rate with no visible
    // difference in the cursor-follow effect.
    var galleryZoomRafPending = false;
    var galleryZoomPendingEvent = null;
    var galleryZoomPendingItem = null;
    document.addEventListener('mousemove', function (event) {
      var item = event.target.closest && event.target.closest('.product-gallery__carousel-item.is-selected');
      if (!item) return;
      galleryZoomPendingEvent = event;
      galleryZoomPendingItem = item;
      if (galleryZoomRafPending) return;
      galleryZoomRafPending = true;
      requestAnimationFrame(function () {
        galleryZoomRafPending = false;
        var media = galleryZoomPendingItem.querySelector('.product-gallery__image');
        if (!media) return;
        var rect = galleryZoomPendingItem.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        var xPct = ((galleryZoomPendingEvent.clientX - rect.left) / rect.width) * 100;
        var yPct = ((galleryZoomPendingEvent.clientY - rect.top) / rect.height) * 100;
        xPct = Math.max(0, Math.min(100, xPct));
        yPct = Math.max(0, Math.min(100, yPct));
        media.style.transformOrigin = xPct + '% ' + yPct + '%';
        media.style.transform = 'scale(' + GALLERY_ZOOM_SCALE + ')';
      });
    });
    // mouseleave doesn't bubble, but a capture-phase listener on document
    // still sees it on the way down to the actual target - the standard
    // way to delegate a non-bubbling event without binding per-image.
    document.addEventListener(
      'mouseleave',
      function (event) {
        var item = event.target.closest && event.target.closest('.product-gallery__carousel-item');
        if (!item) return;
        var media = item.querySelector('.product-gallery__image');
        if (media) media.style.transform = '';
      },
      true
    );
  }
})();
